import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useTheme, useAuth } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Users, UserCheck, Clock, MapPin, CheckCircle2, AlertCircle,
  Search, Plus, RefreshCw, ChevronRight, ChevronLeft, X, Calendar,
  ShieldCheck, Briefcase, Phone, Mail, FileText,
  CheckSquare, Square, Target, ExternalLink,
  ChevronDown, Layers, BarChart3,
  UserPlus, KeyRound, Trash2, Eye, EyeOff,
  Download, Printer, FileSpreadsheet, LayoutGrid, List,
  MoreVertical, Building, Check, ArrowRight, UserX,
  Compass, Award, Sparkles, AlertTriangle, Copy, CheckCircle,
  Camera, Upload
} from "lucide-react";
import { toast } from "sonner";
import { UserAvatar, WhatsAppBlankAvatar } from "@/components/WhatsAppBlankAvatar";

export const resolveMemberDepartment = (member) => {
  const rawDept = (member?.department || "").trim();
  const title = (member?.team_title || "").toLowerCase();

  if (rawDept && rawDept !== "Engineering & Technology" && rawDept !== "Engineering" && rawDept !== "JamaDhan Operations") {
    return rawDept;
  }

  if (title.includes("social media") || title.includes("instagram") || title.includes("community")) {
    return "Marketing & Social Media";
  }
  if (title.includes("executive assistant") || title.includes("assistant")) {
    return "Executive Operations";
  }
  if (title.includes("performance marketing") || title.includes("performance")) {
    return "Growth & Performance Marketing";
  }
  if (title.includes("video") || title.includes("editor") || title.includes("creative") || title.includes("media accounts")) {
    return "Creative Media & Content";
  }
  if (title.includes("seo")) {
    return "SEO & Organic Growth";
  }
  if (title.includes("operation") || title.includes("manager")) {
    return "Business Operations";
  }
  if (title.includes("counselor") || title.includes("support")) {
    return "Student Counseling";
  }
  if (title.includes("mentor") || title.includes("faculty")) {
    return "Trading Faculty";
  }
  if (title.includes("developer") || title.includes("engineer") || title.includes("devops") || title.includes("tech")) {
    return "Engineering & Technology";
  }

  return rawDept || "Operations";
};

export default function AdminTeamManagementPage() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const isLight = theme === "light";

  // Data State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [teamData, setTeamData] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetail, setMemberDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Main Portal Tabs
  const [mainTab, setMainTab] = useState("roster"); // "roster" | "eods" | "tasks"

  // EOD Reports Hub State
  const [eodDate, setEodDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [eodData, setEodData] = useState(null);
  const [loadingEods, setLoadingEods] = useState(false);
  const [selectedEodDept, setSelectedEodDept] = useState("All");
  const [eodStatusFilter, setEodStatusFilter] = useState("all"); // "all" | "submitted" | "pending"
  const [eodSearchQuery, setEodSearchQuery] = useState("");

  // Company Tasks Hub State
  const [companyTasks, setCompanyTasks] = useState([]);
  const [taskStats, setTaskStats] = useState(null);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [taskAssigneeFilter, setTaskAssigneeFilter] = useState("all");
  const [taskStatusFilter, setTaskStatusFilter] = useState("all");
  const [taskPriorityFilter, setTaskPriorityFilter] = useState("all");
  const [taskCategoryFilter, setTaskCategoryFilter] = useState("all");
  const [taskSearchQuery, setTaskSearchQuery] = useState("");

  // Filters & View State
  const [selectedDepartment, setSelectedDepartment] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "table"
  const [detailTab, setDetailTab] = useState("overview"); // "overview" | "attendance" | "tasks" | "reports" | "exports"

  // Modals & Action State
  const [downloadReportModal, setDownloadReportModal] = useState(false);
  const [downloadReportData, setDownloadReportData] = useState(null);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [inspectedImage, setInspectedImage] = useState(null);
  const [memberToDelete, setMemberToDelete] = useState(null);

  // New Task Modal State
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    priority: "P0 (Urgent)",
    category: "Operations",
    timeline_date: new Date().toISOString().split("T")[0],
    checklist_text: "",
    assignee_email: ""
  });
  const [submittingTask, setSubmittingTask] = useState(false);

  // Add Member Modal State
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [addMemberForm, setAddMemberForm] = useState({
    name: "",
    login_id: "",
    password: "",
    phone: "",
    department: "Engineering & Technology",
    team_title: "Software Engineer",
    role: "team_member",
    experience: "2+ Years Experience",
    core_kpis: "Sprint execution, high availability, zero-defect deployment",
    daily_must_do: "Daily standup + active task commits",
    weekly_must_do: "Architecture stability audit",
    escalate_to: "Deepak Sethia"
  });
  const [showAddPassword, setShowAddPassword] = useState(false);
  const [submittingAddMember, setSubmittingAddMember] = useState(false);

  // Edit Member Modal State
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [editMemberTarget, setEditMemberTarget] = useState(null);
  const [editMemberForm, setEditMemberForm] = useState({
    name: "",
    password: "",
    phone: "",
    department: "",
    team_title: "",
    role: "team_member"
  });
  const [submittingEditMember, setSubmittingEditMember] = useState(false);

  // Fetch Team Members
  const fetchTeamMembers = async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/attendance/admin/team-members`, { withCredentials: true });
      if (res.data && res.data.members) {
        setTeamData(res.data);
        if (selectedMember) {
          fetchMemberPerformance(selectedMember.email, false);
        }
      }
    } catch (err) {
      console.error("Error fetching team members:", err);
      toast.error("Failed to load team roster");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch All Daily EOD Reports for Admin Hub
  const fetchEodReports = async (targetDate, targetDept) => {
    setLoadingEods(true);
    try {
      const d = targetDate !== undefined ? targetDate : eodDate;
      const dept = targetDept !== undefined ? targetDept : selectedEodDept;
      const url = `${API}/attendance/admin/all-daily-reports?date=${encodeURIComponent(d)}${dept && dept !== "All" ? `&department=${encodeURIComponent(dept)}` : ""}`;
      const res = await axios.get(url, { withCredentials: true });
      if (res.data && res.data.success) {
        setEodData(res.data);
      }
    } catch (err) {
      console.error("Error loading EOD reports:", err);
      toast.error("Failed to load daily EOD reports");
    } finally {
      setLoadingEods(false);
    }
  };

  // Fetch All Company Tasks for Task Hub
  const fetchAllCompanyTasks = async () => {
    setLoadingTasks(true);
    try {
      const params = new URLSearchParams();
      if (taskStatusFilter && taskStatusFilter !== "all") params.append("status", taskStatusFilter);
      if (taskPriorityFilter && taskPriorityFilter !== "all") params.append("priority", taskPriorityFilter);
      if (taskCategoryFilter && taskCategoryFilter !== "all") params.append("category", taskCategoryFilter);
      if (taskAssigneeFilter && taskAssigneeFilter !== "all") params.append("member_email", taskAssigneeFilter);

      const res = await axios.get(`${API}/attendance/admin/all-tasks?${params.toString()}`, { withCredentials: true });
      if (res.data && res.data.success) {
        setCompanyTasks(res.data.tasks || []);
        setTaskStats({
          total_count: res.data.total_count,
          todo_count: res.data.todo_count,
          in_progress_count: res.data.in_progress_count,
          completed_count: res.data.completed_count,
          p0_count: res.data.p0_count,
        });
      }
    } catch (err) {
      console.error("Error loading company tasks:", err);
    } finally {
      setLoadingTasks(false);
    }
  };

  // EOD Date Navigation Handlers
  const handlePrevEodDay = () => {
    const cur = new Date(eodDate);
    cur.setDate(cur.getDate() - 1);
    const prevStr = cur.toISOString().split("T")[0];
    setEodDate(prevStr);
    fetchEodReports(prevStr);
  };

  const handleNextEodDay = () => {
    const cur = new Date(eodDate);
    cur.setDate(cur.getDate() + 1);
    const nextStr = cur.toISOString().split("T")[0];
    setEodDate(nextStr);
    fetchEodReports(nextStr);
  };

  const handleTodayEod = () => {
    const todayStr = new Date().toISOString().split("T")[0];
    setEodDate(todayStr);
    fetchEodReports(todayStr);
  };

  // Copy Executive EOD Digest to Clipboard
  const copyEodDigest = () => {
    if (!eodData || !eodData.reports) return;
    const submitted = eodData.reports.filter(r => r.eod_report?.has_submitted);
    const pending = eodData.reports.filter(r => !r.eod_report?.has_submitted);

    let text = `📊 JamaDhan Team Daily EOD Report — ${eodData.display_date}\n`;
    text += `Total Team: ${eodData.total_members} | Submitted: ${submitted.length} | Pending: ${pending.length}\n`;
    text += `════════════════════════════════════════════\n\n`;

    if (submitted.length > 0) {
      text += `✅ SUBMITTED REPORTS (${submitted.length}):\n\n`;
      submitted.forEach((r, idx) => {
        text += `${idx + 1}. ${r.name} (${r.team_title} • ${r.department})\n`;
        if (r.attendance.punch_in_time) {
          text += `   ⏰ Shift: ${r.attendance.punch_in_time} – ${r.attendance.punch_out_time || "In Progress"} (${r.attendance.duration_formatted})\n`;
        }
        text += `   📝 Work Summary:\n   ${(r.eod_report.summary || "").split("\n").join("\n   ")}\n`;
        if (r.eod_report.blockers) {
          text += `   ⚠️ Blockers/Notes: ${r.eod_report.blockers}\n`;
        }
        text += `\n`;
      });
    }

    if (pending.length > 0) {
      text += `⏳ PENDING / NOT SUBMITTED (${pending.length}):\n`;
      pending.forEach(r => {
        const shiftStatus = r.attendance.status === "clocked_in" ? "Clocked In (Active)" : (r.attendance.status === "clocked_out" ? "Clocked Out" : "Not Punched Today");
        text += `• ${r.name} (${r.department}) — Shift Status: ${shiftStatus}\n`;
      });
    }

    navigator.clipboard.writeText(text);
    toast.success("Daily EOD Digest copied to clipboard!");
  };

  // Export Day's EOD Report to CSV
  const exportEodCsv = () => {
    if (!eodData || !eodData.reports) return;
    const headers = [
      "Member Name", "Email", "Department", "Team Title", "Punch In Time", "Punch Out Time",
      "Shift Duration", "Attendance Status", "EOD Submitted", "EOD Summary", "Blockers", "Submitted Timestamp"
    ];
    const rows = eodData.reports.map(r => [
      `"${(r.name || "").replace(/"/g, '""')}"`,
      `"${(r.email || "").replace(/"/g, '""')}"`,
      `"${(r.department || "").replace(/"/g, '""')}"`,
      `"${(r.team_title || "").replace(/"/g, '""')}"`,
      `"${r.attendance?.punch_in_time || "--"}"`,
      `"${r.attendance?.punch_out_time || "--"}"`,
      `"${r.attendance?.duration_formatted || "--"}"`,
      `"${r.attendance?.status || "not_punched"}"`,
      `"${r.eod_report?.has_submitted ? "Yes" : "No"}"`,
      `"${(r.eod_report?.summary || "").replace(/"/g, '""').replace(/\n/g, ' ')}"`,
      `"${(r.eod_report?.blockers || "").replace(/"/g, '""').replace(/\n/g, ' ')}"`,
      `"${r.eod_report?.submitted_at || "--"}"`
    ]);
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `jamadhan_eod_reports_${eodData.date}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("EOD CSV downloaded!");
  };

  // Toggle Task Status for Admin Task Hub
  const handleToggleAdminTaskStatus = async (taskId, currentStatus) => {
    if (!taskId || taskId === "undefined") return;
    let nextStatus = "in_progress";
    if (currentStatus === "in_progress") nextStatus = "completed";
    else if (currentStatus === "completed") nextStatus = "todo";

    // Optimistic UI update
    setCompanyTasks(prev => prev.map(t => (t.task_id || t.id || t._id) === taskId ? { ...t, status: nextStatus } : t));

    try {
      await axios.patch(`${API}/attendance/tasks/${encodeURIComponent(taskId)}`, { status: nextStatus }, { withCredentials: true });
      toast.success(nextStatus === "completed" ? "Task marked completed!" : `Task set to ${nextStatus}`);
      fetchAllCompanyTasks();
      fetchTeamMembers(false);
    } catch (err) {
      console.error("Failed to update task status:", err);
      toast.error(err?.response?.data?.detail || "Failed to update task status");
      fetchAllCompanyTasks();
    }
  };

  // Toggle Checklist Item for Admin Task Hub
  const handleToggleAdminChecklistItem = async (task, itemIndex) => {
    const targetId = task.task_id || task.id || task._id;
    if (!task.checklist || !targetId) return;
    const updatedChecklist = task.checklist.map((item, idx) =>
      idx === itemIndex ? { ...item, done: !item.done } : item
    );

    // Optimistic UI update
    setCompanyTasks(prev => prev.map(t => (t.task_id || t.id || t._id) === targetId ? { ...t, checklist: updatedChecklist } : t));

    try {
      await axios.patch(`${API}/attendance/tasks/${encodeURIComponent(targetId)}`, { checklist: updatedChecklist }, { withCredentials: true });
    } catch (err) {
      console.error("Failed to update checklist item:", err);
      toast.error(err?.response?.data?.detail || "Failed to update checklist item");
      fetchAllCompanyTasks();
    }
  };

  // Delete Task for Admin Task Hub
  const handleDeleteAdminTask = async (taskId) => {
    if (!taskId || taskId === "undefined") {
      toast.error("Invalid task identifier");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this task?")) return;

    // Immediate optimistic update: removes from screen instantly
    setCompanyTasks(prev => prev.filter(t => (t.task_id || t.id || t._id) !== taskId));

    try {
      await axios.delete(`${API}/attendance/tasks/${encodeURIComponent(taskId)}`, { withCredentials: true });
      toast.success("Task deleted successfully");
      fetchAllCompanyTasks();
      fetchTeamMembers(false);
    } catch (err) {
      console.error("Failed to delete task:", err);
      toast.error(err?.response?.data?.detail || "Failed to delete task");
      fetchAllCompanyTasks();
    }
  };

  useEffect(() => {
    fetchTeamMembers();
    fetchEodReports();
    fetchAllCompanyTasks();
  }, []);

  // Fetch Member Detail
  const fetchMemberPerformance = async (emailOrId, openModal = true) => {
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${API}/attendance/admin/member-performance/${encodeURIComponent(emailOrId)}`, { withCredentials: true });
      if (res.data && res.data.success) {
        setMemberDetail(res.data);
        if (openModal) {
          setSelectedMember(res.data.member);
        }
      }
    } catch (err) {
      console.error("Error fetching member performance:", err);
      toast.error("Could not fetch member performance details");
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSelectMember = (member) => {
    setSelectedMember(member);
    setDetailTab("overview");
    fetchMemberPerformance(member.email, true);
  };

  const handleAdminDownloadReport = async (email, days) => {
    setLoadingDownload(true);
    try {
      const res = await axios.get(`${API}/attendance/reports/download?days=${days}&user_email=${encodeURIComponent(email)}`, { withCredentials: true });
      if (res.data?.success) {
        setDownloadReportData(res.data);
        setDownloadReportModal(true);
      }
    } catch (err) {
      toast.error("Failed to generate report");
    } finally {
      setLoadingDownload(false);
    }
  };

  const exportReportToCSV = () => {
    if (!downloadReportData || !downloadReportData.records) return;
    const headers = ["Date", "Day", "Punch In Time", "Punch Out Time", "Duration", "Status", "Verified Location", "Daily EOD Work Report"];
    const rows = downloadReportData.records.map(r => [
      r.date,
      r.day,
      r.punch_in_time,
      r.punch_out_time,
      r.duration,
      r.status,
      `"${(r.location || '').replace(/"/g, '""')}"`,
      `"${(r.eod_report || '').replace(/"/g, '""')}"`
    ]);
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `jamadhan_report_${downloadReportData.member?.name || 'staff'}_${downloadReportData.period_days}days.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV report downloaded!");
  };

  // Assign Task
  const handleAssignTask = async (e) => {
    e.preventDefault();
    if (!taskForm.title.trim()) {
      toast.error("Please enter a task title");
      return;
    }
    const targetMember = (members && members.find(m => m.email === taskForm.assignee_email)) || selectedMember || (members && members[0]);
    if (!targetMember) {
      toast.error("Please select a team member");
      return;
    }

    setSubmittingTask(true);
    try {
      const checklistItems = taskForm.checklist_text
        .split("\n")
        .map(t => t.trim())
        .filter(Boolean)
        .map((text, idx) => ({ id: `c_${Date.now()}_${idx}`, text, done: false }));

      const payload = {
        title: taskForm.title.trim(),
        description: taskForm.description.trim(),
        assigned_to_email: targetMember.email,
        assigned_to_name: targetMember.name,
        priority: taskForm.priority,
        category: taskForm.category,
        timeline_date: taskForm.timeline_date,
        checklist: checklistItems
      };

      const res = await axios.post(`${API}/attendance/admin/assign-task`, payload);
      if (res.data && res.data.success) {
        toast.success(`Task assigned to ${targetMember.name}!`);
        setShowAssignModal(false);
        setTaskForm({
          title: "",
          description: "",
          priority: "P0 (Urgent)",
          category: "Operations",
          timeline_date: new Date().toISOString().split("T")[0],
          checklist_text: "",
          assignee_email: ""
        });
        if (selectedMember) fetchMemberPerformance(selectedMember.email, false);
        fetchTeamMembers(false);
        fetchAllCompanyTasks();
      }
    } catch (err) {
      console.error("Error assigning task:", err);
      toast.error("Failed to assign task");
    } finally {
      setSubmittingTask(false);
    }
  };

  // Add Member
  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!addMemberForm.name.trim() || !addMemberForm.login_id.trim() || !addMemberForm.password.trim()) {
      toast.error("Please provide Full Name, User ID/Email, and Password");
      return;
    }
    setSubmittingAddMember(true);
    try {
      const res = await axios.post(`${API}/attendance/admin/team-members`, addMemberForm);
      if (res.data?.success) {
        toast.success(res.data.message || "Team member provisioned successfully!");
        setShowAddMemberModal(false);
        setAddMemberForm({
          name: "",
          login_id: "",
          password: "",
          phone: "",
          department: "Engineering & Technology",
          team_title: "Software Engineer",
          role: "team_member",
          experience: "2+ Years Experience",
          core_kpis: "Sprint execution, high availability, zero-defect deployment",
          daily_must_do: "Daily standup + active task commits",
          weekly_must_do: "Architecture stability audit",
          escalate_to: "Deepak Sethia"
        });
        fetchTeamMembers(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create team member");
    } finally {
      setSubmittingAddMember(false);
    }
  };

  // Open Edit / Reset Password Modal
  const handleOpenEditMember = (member, e) => {
    if (e) e.stopPropagation();
    setEditMemberTarget(member);
    setEditMemberForm({
      name: member.name || "",
      password: "",
      phone: member.phone || "",
      department: member.department || "Operations",
      team_title: member.team_title || "Team Member",
      role: member.role || "team_member"
    });
    setShowEditMemberModal(true);
  };

  // Update Member Credentials / Profile
  const handleUpdateMember = async (e) => {
    e.preventDefault();
    if (!editMemberTarget) return;
    setSubmittingEditMember(true);
    try {
      const payload = {
        name: editMemberForm.name,
        phone: editMemberForm.phone,
        department: editMemberForm.department,
        team_title: editMemberForm.team_title,
        role: editMemberForm.role
      };
      if (editMemberForm.password.trim()) {
        payload.password = editMemberForm.password.trim();
      }
      const res = await axios.put(`${API}/attendance/admin/team-members/${encodeURIComponent(editMemberTarget.email)}`, payload);
      if (res.data?.success) {
        toast.success("Team member details updated!");
        setShowEditMemberModal(false);
        fetchTeamMembers(true);
        if (selectedMember && selectedMember.email === editMemberTarget.email) {
          fetchMemberPerformance(editMemberTarget.email, false);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update member");
    } finally {
      setSubmittingEditMember(false);
    }
  };

  // Upload custom photo for a team member
  const handleUploadMemberPhoto = async (targetEmail, file) => {
    if (!file || !targetEmail) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Image file must be under 6MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64Data = e.target.result;
      try {
        await axios.post(`${API}/attendance/profile/picture`, {
          email: targetEmail,
          picture: base64Data
        }, { withCredentials: true });
        toast.success("Profile picture updated!");
        fetchTeamMembers(false);
        if (selectedMember && selectedMember.email === targetEmail) {
          setSelectedMember(prev => ({ ...prev, picture: base64Data }));
        }
      } catch (err) {
        toast.error("Failed to update profile picture");
      }
    };
    reader.readAsDataURL(file);
  };

  // Remove photo and revert back to WhatsApp blank DP
  const handleRemoveMemberPhoto = async (targetEmail) => {
    if (!targetEmail) return;
    try {
      await axios.post(`${API}/attendance/profile/picture`, {
        email: targetEmail,
        picture: ""
      }, { withCredentials: true });
      toast.success("Profile picture reset to blank DP");
      fetchTeamMembers(false);
      if (selectedMember && selectedMember.email === targetEmail) {
        setSelectedMember(prev => ({ ...prev, picture: "" }));
      }
    } catch {
      toast.error("Failed to reset profile picture");
    }
  };

  // Confirm Delete Member
  const confirmDeleteMember = async () => {
    if (!memberToDelete) return;
    try {
      await axios.delete(`${API}/attendance/admin/team-members/${encodeURIComponent(memberToDelete.email)}`);
      toast.success(`${memberToDelete.name} removed from roster`);
      if (selectedMember?.email === memberToDelete.email) {
        setSelectedMember(null);
        setMemberDetail(null);
      }
      setMemberToDelete(null);
      fetchTeamMembers(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove member");
    }
  };

  // Toggle Checklist Item
  const handleToggleChecklist = async (taskId, checklist, itemIndex) => {
    if (!checklist) return;
    const updatedChecklist = checklist.map((item, idx) =>
      idx === itemIndex ? { ...item, done: !item.done } : item
    );
    try {
      await axios.patch(`${API}/attendance/tasks/${taskId}`, {
        checklist: updatedChecklist
      });
      setMemberDetail(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          tasks: prev.tasks.map(t =>
            t.task_id === taskId
              ? {
                  ...t,
                  checklist: updatedChecklist,
                  progress_percent: Math.round(
                    (updatedChecklist.filter(c => c.done).length / updatedChecklist.length) * 100
                  )
                }
              : t
          )
        };
      });
    } catch (err) {
      toast.error("Failed to update checklist item");
    }
  };

  // Memoized lists & statistics
  const members = useMemo(() => {
    return (teamData?.members || []).map(m => ({
      ...m,
      department: resolveMemberDepartment(m)
    }));
  }, [teamData?.members]);

  const departments = useMemo(() => {
    const set = new Set(members.map(m => m.department).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [members]);

  const stats = useMemo(() => {
    const total = members.length;
    const clockedIn = members.filter(m => m.today?.status === "clocked_in").length;
    const clockedOut = members.filter(m => m.today?.status === "clocked_out").length;
    const notPunched = members.filter(m => !m.today?.status || m.today?.status === "not_punched").length;
    const activeTasks = members.reduce((acc, m) => acc + (m.tasks_summary?.active_count || 0), 0);
    const complianceRate = total > 0 ? Math.round(((clockedIn + clockedOut) / total) * 100) : 0;
    return { total, clockedIn, clockedOut, notPunched, activeTasks, complianceRate };
  }, [members]);

  const filteredMembers = useMemo(() => {
    return members.filter(m => {
      const matchesDept = selectedDepartment === "All" || m.department === selectedDepartment;
      const status = m.today?.status || "not_punched";
      const matchesStatus =
        selectedStatus === "All" ||
        (selectedStatus === "clocked_in" && status === "clocked_in") ||
        (selectedStatus === "clocked_out" && status === "clocked_out") ||
        (selectedStatus === "not_punched" && status === "not_punched");
      const matchesSearch =
        !searchQuery.trim() ||
        (m.name && m.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (m.team_title && m.team_title.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (m.email && m.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (m.department && m.department.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesDept && matchesStatus && matchesSearch;
    });
  }, [members, selectedDepartment, selectedStatus, searchQuery]);

  return (
    <DashboardLayout title="Team Operations">
      <div className="space-y-6 max-w-7xl mx-auto pb-16 font-sans">
        
        {/* ===================== TOP HEADER & BREADCRUMB ===================== */}
        <div className={`p-6 rounded-2xl border transition-all ${
          isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800 shadow-lg"
        }`}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs font-medium text-slate-500 mb-1.5">
                <span>Operations</span>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <span className={isLight ? "text-slate-900 font-semibold" : "text-zinc-200 font-semibold"}>
                  Team Directory & Live Attendance
                </span>
              </div>
              <div className="flex items-center gap-3">
                <h1 className={`text-2xl md:text-3xl font-bold tracking-tight ${
                  isLight ? "text-slate-900" : "text-white"
                }`}>
                  Team Operations Console
                </h1>
                <Badge variant="outline" className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                  isLight ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-emerald-950/60 text-emerald-400 border-emerald-800"
                }`}>
                  <ShieldCheck className="w-3 h-3 mr-1 inline" />
                  Live Audits Active
                </Badge>
              </div>
              <p className={`text-xs md:text-sm mt-1.5 flex items-center gap-2 ${
                isLight ? "text-slate-500" : "text-zinc-400"
              }`}>
                <span>Jaipur campus biometric verification, daily punch-in/out timestamps, task SLAs, and executive performance dossiers.</span>
                <span className="hidden sm:inline text-slate-300">•</span>
                <span className={`hidden sm:inline text-xs font-mono px-2 py-0.5 rounded ${
                  isLight ? "bg-slate-100 text-slate-700" : "bg-zinc-800 text-zinc-300"
                }`}>
                  IST Time Sync (UTC+5:30)
                </span>
              </p>
            </div>

            {/* Action Bar */}
            <div className="flex items-center gap-2.5 shrink-0 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchTeamMembers(true)}
                disabled={refreshing}
                className={`h-9 text-xs font-medium rounded-xl border ${
                  isLight 
                    ? "bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900" 
                    : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700 hover:text-white"
                }`}
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin text-emerald-600" : "text-slate-500"}`} />
                Sync Live
              </Button>
              <Button
                size="sm"
                onClick={() => setShowAddMemberModal(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs h-9 px-3.5 rounded-xl shadow-xs transition-colors flex items-center gap-1.5"
              >
                <UserPlus className="w-4 h-4" />
                Add Member
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (members.length > 0) {
                    setSelectedMember(members[0]);
                    setShowAssignModal(true);
                  }
                }}
                className={`h-9 text-xs font-medium rounded-xl border ${
                  isLight
                    ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    : "border-emerald-800 text-emerald-400 hover:bg-emerald-950/40"
                }`}
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Assign Task
              </Button>
            </div>
          </div>
        </div>

        {/* ===================== TOP LEVEL NAVIGATION TABS ===================== */}
        <div className="flex items-center gap-2 border-b border-slate-200 dark:border-zinc-800 pb-3 overflow-x-auto">
          <button
            onClick={() => setMainTab("roster")}
            className={`px-4 py-2.5 rounded-xl font-medium text-xs sm:text-sm transition-all flex items-center gap-2 shrink-0 ${
              mainTab === "roster"
                ? "bg-slate-900 text-white shadow-sm dark:bg-white dark:text-slate-900 font-semibold"
                : "bg-white dark:bg-zinc-900 text-slate-600 dark:text-zinc-400 hover:text-slate-900 border border-slate-200 dark:border-zinc-800"
            }`}
          >
            <Users className="w-4 h-4" />
            <span>Team Roster & Attendance</span>
            <span className={`ml-1 text-[11px] px-2 py-0.5 rounded-full font-mono ${mainTab === "roster" ? "bg-white/20 text-white dark:bg-slate-900/10 dark:text-slate-900" : "bg-slate-100 dark:bg-zinc-800 text-slate-600"}`}>
              {stats.total}
            </span>
          </button>

          <button
            onClick={() => {
              setMainTab("eods");
              if (!eodData) fetchEodReports();
            }}
            className={`px-4 py-2.5 rounded-xl font-medium text-xs sm:text-sm transition-all flex items-center gap-2 shrink-0 ${
              mainTab === "eods"
                ? "bg-emerald-600 text-white shadow-sm font-semibold"
                : "bg-white dark:bg-zinc-900 text-slate-600 dark:text-zinc-400 hover:text-slate-900 border border-slate-200 dark:border-zinc-800"
            }`}
          >
            <FileText className="w-4 h-4" />
            <span>Daily EOD Reports Hub</span>
            {eodData && (
              <span className={`ml-1 text-[11px] px-2 py-0.5 rounded-full font-mono ${mainTab === "eods" ? "bg-white/25 text-white" : "bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400"}`}>
                {eodData.submitted_count}/{eodData.total_members}
              </span>
            )}
          </button>

          <button
            onClick={() => {
              setMainTab("tasks");
              if (!companyTasks.length) fetchAllCompanyTasks();
            }}
            className={`px-4 py-2.5 rounded-xl font-medium text-xs sm:text-sm transition-all flex items-center gap-2 shrink-0 ${
              mainTab === "tasks"
                ? "bg-indigo-600 text-white shadow-sm font-semibold"
                : "bg-white dark:bg-zinc-900 text-slate-600 dark:text-zinc-400 hover:text-slate-900 border border-slate-200 dark:border-zinc-800"
            }`}
          >
            <CheckSquare className="w-4 h-4" />
            <span>Company Task Hub</span>
            {taskStats && (
              <span className={`ml-1 text-[11px] px-2 py-0.5 rounded-full font-mono ${mainTab === "tasks" ? "bg-white/25 text-white" : "bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-400"}`}>
                {taskStats.todo_count + taskStats.in_progress_count} Active
              </span>
            )}
          </button>
        </div>

        {/* ===================== TAB 1: TEAM ROSTER & ATTENDANCE ===================== */}
        {mainTab === "roster" && (
          <div className="space-y-6 animate-in fade-in duration-200">
            {/* Metric Cards (Stripe/Apple Style) */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
          {/* Total Roster */}
          <Card className={`rounded-2xl border transition-all ${
            isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800 shadow-md"
          }`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium uppercase tracking-wider ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                  Total Team Roster
                </p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className={`text-2xl font-bold tracking-tight font-mono ${isLight ? "text-slate-900" : "text-white"}`}>
                    {stats.total}
                  </span>
                  <span className={`text-xs ${isLight ? "text-slate-500" : "text-zinc-400"}`}>Active Members</span>
                </div>
                <p className={`text-[11px] mt-0.5 ${isLight ? "text-slate-400" : "text-zinc-500"}`}>
                  {departments.length - 1} Departments
                </p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isLight ? "bg-slate-100 text-slate-700" : "bg-zinc-800 text-zinc-300"
              }`}>
                <Users className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>

          {/* Clocked In Now */}
          <Card className={`rounded-2xl border transition-all ${
            isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800 shadow-md"
          }`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium uppercase tracking-wider flex items-center gap-1.5 ${
                  isLight ? "text-emerald-700" : "text-emerald-400"
                }`}>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  Clocked In On-Site
                </p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className={`text-2xl font-bold tracking-tight font-mono ${
                    isLight ? "text-emerald-700" : "text-emerald-400"
                  }`}>
                    {stats.clockedIn}
                  </span>
                  <span className={`text-xs ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                    / {stats.total} On-Site
                  </span>
                </div>
                <p className={`text-[11px] mt-0.5 ${isLight ? "text-slate-400" : "text-zinc-500"}`}>
                  Tagore Nagar Campus
                </p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isLight ? "bg-emerald-50 text-emerald-600 border border-emerald-100" : "bg-emerald-950/60 text-emerald-400 border border-emerald-900"
              }`}>
                <UserCheck className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>

          {/* Attendance Compliance */}
          <Card className={`rounded-2xl border transition-all ${
            isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800 shadow-md"
          }`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium uppercase tracking-wider ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                  Today's Compliance
                </p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className={`text-2xl font-bold tracking-tight font-mono ${isLight ? "text-slate-900" : "text-white"}`}>
                    {stats.complianceRate}%
                  </span>
                  <span className={`text-xs font-medium ${isLight ? "text-emerald-600" : "text-emerald-400"}`}>
                    {stats.clockedOut} Completed
                  </span>
                </div>
                <p className={`text-[11px] mt-0.5 ${isLight ? "text-slate-400" : "text-zinc-500"}`}>
                  {stats.notPunched} Unpunched Today
                </p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isLight ? "bg-blue-50 text-blue-600 border border-blue-100" : "bg-blue-950/60 text-blue-400 border border-blue-900"
              }`}>
                <ShieldCheck className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>

          {/* Active Operational Tasks */}
          <Card className={`rounded-2xl border transition-all ${
            isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800 shadow-md"
          }`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium uppercase tracking-wider ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                  Active Tasks & SLAs
                </p>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className={`text-2xl font-bold tracking-tight font-mono ${
                    isLight ? "text-amber-600" : "text-amber-400"
                  }`}>
                    {stats.activeTasks}
                  </span>
                  <span className={`text-xs ${isLight ? "text-slate-500" : "text-zinc-400"}`}>Live Deliverables</span>
                </div>
                <p className={`text-[11px] mt-0.5 ${isLight ? "text-slate-400" : "text-zinc-500"}`}>
                  Across all departments
                </p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isLight ? "bg-amber-50 text-amber-600 border border-amber-100" : "bg-amber-950/60 text-amber-400 border border-amber-900"
              }`}>
                <CheckSquare className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ===================== FILTER, SEARCH & VIEW TOOLBAR ===================== */}
        <div className={`p-3.5 rounded-2xl border transition-all flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 ${
          isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800"
        }`}>
          {/* Department Filter Pills */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0 text-xs custom-scrollbar">
            {departments.map((dept) => {
              const isSelected = selectedDepartment === dept;
              return (
                <button
                  key={dept}
                  onClick={() => setSelectedDepartment(dept)}
                  className={`px-3 py-1.5 rounded-xl font-medium transition-all shrink-0 text-xs ${
                    isSelected
                      ? isLight
                        ? "bg-slate-900 text-white font-semibold shadow-xs"
                        : "bg-white text-zinc-900 font-semibold shadow-xs"
                      : isLight
                        ? "bg-slate-50 text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-slate-200/60"
                        : "bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700 border border-zinc-700/60"
                  }`}
                >
                  {dept}
                </button>
              );
            })}
          </div>

          {/* Right Controls: Status Filter, Search, View Mode */}
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className={`text-xs rounded-xl px-2.5 py-1.5 border outline-none font-medium ${
                isLight
                  ? "bg-slate-50 border-slate-200 text-slate-700 focus:border-slate-400"
                  : "bg-zinc-800 border-zinc-700 text-zinc-300 focus:border-zinc-500"
              }`}
            >
              <option value="All">All Statuses ({members.length})</option>
              <option value="clocked_in">Clocked In Now ({stats.clockedIn})</option>
              <option value="clocked_out">Checked Out Today ({stats.clockedOut})</option>
              <option value="not_punched">Not Punched Today ({stats.notPunched})</option>
            </select>

            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                type="text"
                placeholder="Search member, role, email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`pl-8 pr-7 h-8 text-xs rounded-xl w-44 sm:w-56 ${
                  isLight
                    ? "bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 focus:bg-white"
                    : "bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                }`}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Grid / Table View Switcher */}
            <div className={`flex items-center p-0.5 rounded-xl border ${
              isLight ? "bg-slate-100 border-slate-200/80" : "bg-zinc-800 border-zinc-700"
            }`}>
              <button
                onClick={() => setViewMode("grid")}
                title="Grid Cards View"
                className={`p-1.5 rounded-lg transition-colors ${
                  viewMode === "grid"
                    ? isLight ? "bg-white text-slate-900 shadow-xs" : "bg-zinc-700 text-white"
                    : "text-slate-400 hover:text-slate-700"
                }`}
              >
                <LayoutGrid className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setViewMode("table")}
                title="Executive Table View"
                className={`p-1.5 rounded-lg transition-colors ${
                  viewMode === "table"
                    ? isLight ? "bg-white text-slate-900 shadow-xs" : "bg-zinc-700 text-white"
                    : "text-slate-400 hover:text-slate-700"
                }`}
              >
                <List className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>

        {/* ===================== TEAM DIRECTORY VIEW ===================== */}
        {loading ? (
          <div className="py-24 text-center">
            <RefreshCw className="w-7 h-7 animate-spin mx-auto text-emerald-600 mb-3" />
            <p className={`text-sm font-medium ${isLight ? "text-slate-600" : "text-zinc-400"}`}>
              Syncing live attendance audits and team roster...
            </p>
          </div>
        ) : members.length === 0 ? (
          <div className={`py-20 text-center rounded-3xl border p-8 max-w-xl mx-auto shadow-sm ${
            isLight ? "bg-white border-slate-200" : "bg-zinc-900 border-zinc-800"
          }`}>
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
              isLight ? "bg-emerald-50 text-emerald-600 border border-emerald-100" : "bg-emerald-950/60 text-emerald-400 border border-emerald-900"
            }`}>
              <UserPlus className="w-7 h-7" />
            </div>
            <h3 className={`text-lg font-bold ${isLight ? "text-slate-900" : "text-white"}`}>
              No Team Members Registered
            </h3>
            <p className={`text-xs leading-relaxed mt-2 max-w-md mx-auto ${
              isLight ? "text-slate-500" : "text-zinc-400"
            }`}>
              Add team members to enable mobile biometric punch-in, GPS geofencing, daily task management, and end-of-day executive work reports.
            </p>
            <Button
              onClick={() => setShowAddMemberModal(true)}
              className="mt-5 bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs px-5 py-2 rounded-xl shadow-xs"
            >
              <UserPlus className="w-4 h-4 mr-1.5" />
              Add First Team Member
            </Button>
          </div>
        ) : filteredMembers.length === 0 ? (
          <div className={`py-16 text-center rounded-2xl border ${
            isLight ? "bg-white border-slate-200" : "bg-zinc-900 border-zinc-800"
          }`}>
            <Users className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className={`text-sm font-semibold ${isLight ? "text-slate-800" : "text-zinc-200"}`}>
              No team members match your criteria
            </p>
            <p className="text-xs text-slate-400 mt-1">Try clearing search or changing the department filter</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedDepartment("All");
                setSelectedStatus("All");
                setSearchQuery("");
              }}
              className="mt-3 text-xs"
            >
              Reset Filters
            </Button>
          </div>
        ) : viewMode === "grid" ? (
          /* ===================== GRID CARDS VIEW ===================== */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMembers.map((member) => {
              const isClockedIn = member.today?.status === "clocked_in";
              const isClockedOut = member.today?.status === "clocked_out";

              return (
                <div
                  key={member.email}
                  onClick={() => handleSelectMember(member)}
                  className={`group relative p-5 rounded-2xl border transition-all duration-200 cursor-pointer flex flex-col justify-between ${
                    isLight
                      ? "bg-white border-slate-200/80 hover:border-slate-300 hover:shadow-md"
                      : "bg-zinc-900/90 border-zinc-800 hover:border-zinc-700 hover:shadow-lg"
                  }`}
                >
                  {/* Top card section */}
                  <div>
                    {/* Header Row: Avatar, Name, Status Badge */}
                    <div className="flex items-start justify-between gap-3 mb-3.5">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="relative shrink-0">
                          <UserAvatar
                            src={member.picture}
                            alt={member.name}
                            className={`w-11 h-11 rounded-2xl border ${isLight ? "border-slate-200" : "border-zinc-700"}`}
                          />
                          <span
                            className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 ${
                              isLight ? "border-white" : "border-zinc-900"
                            } ${
                              isClockedIn
                                ? "bg-emerald-500 ring-2 ring-emerald-400/30"
                                : isClockedOut
                                ? "bg-amber-500"
                                : "bg-slate-400"
                            }`}
                          />
                        </div>

                        <div className="min-w-0">
                          <h3 className={`font-semibold text-sm truncate leading-snug group-hover:text-emerald-700 transition-colors ${
                            isLight ? "text-slate-900" : "text-white"
                          }`}>
                            {member.name}
                          </h3>
                          <p className={`text-xs truncate ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                            {member.team_title || "Team Member"}
                          </p>
                        </div>
                      </div>

                      {/* Status Badge */}
                      <Badge
                        variant="outline"
                        className={`text-[10px] font-medium px-2 py-0.5 shrink-0 rounded-full ${
                          isClockedIn
                            ? isLight ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-emerald-950/60 text-emerald-400 border-emerald-800"
                            : isClockedOut
                            ? isLight ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-amber-950/60 text-amber-400 border-amber-800"
                            : isLight ? "bg-slate-50 text-slate-600 border-slate-200" : "bg-zinc-800 text-zinc-400 border-zinc-700"
                        }`}
                      >
                        {isClockedIn ? "Clocked In" : isClockedOut ? "Checked Out" : "Not Punched"}
                      </Badge>
                    </div>

                    {/* Department & Escalation */}
                    <div className="flex items-center gap-1.5 flex-wrap mb-3.5">
                      <span className={`text-[11px] font-medium px-2.5 py-0.5 rounded-lg border ${
                        isLight ? "bg-slate-50 text-slate-700 border-slate-200/60" : "bg-zinc-800 text-zinc-300 border-zinc-700/60"
                      }`}>
                        {member.department}
                      </span>
                      {member.escalate_to && member.escalate_to !== "-" && (
                        <span className={`text-[10px] px-2 py-0.5 rounded-lg border ${
                          isLight ? "bg-blue-50 text-blue-700 border-blue-200/60" : "bg-blue-950/40 text-blue-400 border-blue-900/60"
                        }`}>
                          Escalate: {member.escalate_to}
                        </span>
                      )}
                    </div>

                    {/* Today's Punch Information Box */}
                    <div className={`p-3 rounded-xl border space-y-1.5 mb-3.5 ${
                      isLight ? "bg-slate-50/70 border-slate-100" : "bg-zinc-950/60 border-zinc-800/80"
                    }`}>
                      <div className="flex items-center justify-between text-xs">
                        <span className={`flex items-center gap-1.5 ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                          <Clock className="w-3.5 h-3.5 text-emerald-600" />
                          Check-In:
                        </span>
                        <span className={`font-mono font-semibold ${isLight ? "text-slate-900" : "text-white"}`}>
                          {member.today?.punch_in_time || "—"}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-xs">
                        <span className={`flex items-center gap-1.5 ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                          <Clock className="w-3.5 h-3.5 text-amber-500" />
                          Check-Out:
                        </span>
                        <span className={`font-mono font-medium ${isLight ? "text-slate-700" : "text-zinc-300"}`}>
                          {member.today?.punch_out_time || (isClockedIn ? "In Progress" : "—")}
                        </span>
                      </div>

                      <div className={`flex items-center justify-between text-[11px] pt-1.5 border-t ${
                        isLight ? "border-slate-200/60" : "border-zinc-800"
                      }`}>
                        <span className={`truncate max-w-[170px] flex items-center gap-1 ${
                          isLight ? "text-slate-500" : "text-zinc-400"
                        }`}>
                          <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                          {member.today?.location_name?.split("(")[0]?.trim() || "Jaipur Campus"}
                        </span>
                        <span className={`font-mono font-medium shrink-0 ${
                          member.today?.punch_in_time ? "text-emerald-600" : "text-slate-400"
                        }`}>
                          {member.today?.punch_in_time ? "Onsite Verified" : "Pending"}
                        </span>
                      </div>
                    </div>

                    {/* Active Tasks & KPI Summary */}
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center justify-between">
                        <span className={`text-[11px] ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                          Active Deliverables:
                        </span>
                        <span className={`font-semibold font-mono text-xs ${
                          (member.tasks_summary?.active_count || 0) > 0 ? "text-amber-600" : "text-slate-400"
                        }`}>
                          {(member.tasks_summary?.active_count || 0) > 0
                            ? `${member.tasks_summary.active_count} In Progress`
                            : (member.tasks_summary?.total_count || 0) > 0
                              ? `${member.tasks_summary.total_count} In Queue`
                              : "0 Tasks"}
                        </span>
                      </div>
                      <div className={`flex items-start gap-1 text-[11px] pt-0.5 truncate ${
                        isLight ? "text-slate-500" : "text-zinc-400"
                      }`}>
                        <Target className="w-3 h-3 text-slate-400 shrink-0 mt-0.5" />
                        <span className="truncate italic">{member.core_kpis || "Standard SLAs"}</span>
                      </div>
                    </div>
                  </div>

                  {/* Card Footer: Compliance & CTA */}
                  <div className={`pt-3.5 mt-3.5 border-t flex items-center justify-between text-xs ${
                    isLight ? "border-slate-100" : "border-zinc-800"
                  }`}>
                    <span className={`font-mono text-[11px] ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                      Attendance: <strong className={isLight ? "text-slate-800" : "text-white"}>{member.metrics?.attendance_rate || 96}%</strong>
                    </span>
                    <span className="text-emerald-700 font-semibold flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                      View Dossier <ChevronRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* ===================== TABLE VIEW (HIGH-DENSITY ENTERPRISE) ===================== */
          <div className={`rounded-2xl border overflow-hidden ${
            isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900 border-zinc-800"
          }`}>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className={`border-b text-[11px] font-semibold uppercase tracking-wider ${
                  isLight ? "bg-slate-50/80 text-slate-500 border-slate-200/80" : "bg-zinc-800/80 text-zinc-400 border-zinc-700"
                }`}>
                  <tr>
                    <th className="py-3 px-4">Member</th>
                    <th className="py-3 px-4">Role & Dept</th>
                    <th className="py-3 px-4">Shift Status</th>
                    <th className="py-3 px-4">Check-In (IST)</th>
                    <th className="py-3 px-4">Check-Out (IST)</th>
                    <th className="py-3 px-4">Location</th>
                    <th className="py-3 px-4">Tasks</th>
                    <th className="py-3 px-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isLight ? "divide-slate-100" : "divide-zinc-800"}`}>
                  {filteredMembers.map((member) => {
                    const isClockedIn = member.today?.status === "clocked_in";
                    const isClockedOut = member.today?.status === "clocked_out";

                    return (
                      <tr
                        key={member.email}
                        onClick={() => handleSelectMember(member)}
                        className={`transition-colors cursor-pointer ${
                          isLight ? "hover:bg-slate-50/70" : "hover:bg-zinc-800/50"
                        }`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2.5">
                            <UserAvatar
                              src={member.picture}
                              alt={member.name}
                              className="w-8 h-8 rounded-full"
                            />
                            <div>
                              <p className={`font-semibold ${isLight ? "text-slate-900" : "text-white"}`}>
                                {member.name}
                              </p>
                              <p className="text-[11px] text-slate-400 font-mono">{member.email}</p>
                            </div>
                          </div>
                        </td>

                        <td className="py-3 px-4">
                          <p className={`font-medium ${isLight ? "text-slate-800" : "text-zinc-200"}`}>
                            {member.team_title || "Team Member"}
                          </p>
                          <p className="text-[11px] text-slate-400">{member.department}</p>
                        </td>

                        <td className="py-3 px-4">
                          <Badge
                            variant="outline"
                            className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                              isClockedIn
                                ? isLight ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-emerald-950/60 text-emerald-400 border-emerald-800"
                                : isClockedOut
                                ? isLight ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-amber-950/60 text-amber-400 border-amber-800"
                                : isLight ? "bg-slate-50 text-slate-600 border-slate-200" : "bg-zinc-800 text-zinc-400 border-zinc-700"
                            }`}
                          >
                            {isClockedIn ? "Clocked In" : isClockedOut ? "Checked Out" : "Not Punched"}
                          </Badge>
                        </td>

                        <td className="py-3 px-4 font-mono font-medium text-slate-700">
                          {member.today?.punch_in_time || "—"}
                        </td>

                        <td className="py-3 px-4 font-mono text-slate-600">
                          {member.today?.punch_out_time || (isClockedIn ? "Active" : "—")}
                        </td>

                        <td className="py-3 px-4 text-slate-500 text-[11px] truncate max-w-[140px]">
                          {member.today?.location_name?.split("(")[0]?.trim() || "Jaipur Campus"}
                        </td>

                        <td className="py-3 px-4 font-mono">
                          <span className={member.tasks_summary?.active_count ? "text-amber-600 font-semibold" : "text-slate-400"}>
                            {member.tasks_summary?.active_count || 0} active
                          </span>
                        </td>

                        <td className="py-3 px-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 text-xs h-7 px-2 font-medium"
                          >
                            Dossier →
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    )}

    {/* ========================================================================= */}
    {/* ===================== TAB 2: DAILY EOD REPORTS HUB ===================== */}
    {/* ========================================================================= */}
    {mainTab === "eods" && (
      <div className="space-y-6 animate-in fade-in duration-200">
        {/* Date Navigator & Controls */}
        <div className={`p-4 sm:p-5 rounded-2xl border transition-all flex flex-col md:flex-row md:items-center justify-between gap-4 ${
          isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800 shadow-md"
        }`}>
          {/* Date Nav Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevEodDay}
              className="h-9 px-3 rounded-xl border text-xs"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border bg-slate-50 dark:bg-zinc-800 border-slate-200 dark:border-zinc-700">
              <Calendar className="w-4 h-4 text-emerald-600" />
              <input
                type="date"
                value={eodDate}
                onChange={(e) => {
                  if (e.target.value) {
                    setEodDate(e.target.value);
                    fetchEodReports(e.target.value);
                  }
                }}
                className="bg-transparent text-xs font-mono font-semibold text-slate-800 dark:text-zinc-200 outline-none cursor-pointer"
              />
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={handleNextEodDay}
              className="h-9 px-3 rounded-xl border text-xs"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleTodayEod}
              className="h-9 px-3 rounded-xl text-xs font-semibold text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950"
            >
              Today
            </Button>
          </div>

          {/* Day Display & Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-700 dark:text-zinc-300 mr-2 hidden lg:inline">
              {eodData?.display_date || eodDate}
            </span>

            <Button
              variant="outline"
              size="sm"
              onClick={copyEodDigest}
              disabled={!eodData?.reports?.length}
              className="h-9 text-xs font-medium rounded-xl border flex items-center gap-1.5"
            >
              <Copy className="w-3.5 h-3.5 text-slate-500" />
              Copy Digest
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={exportEodCsv}
              disabled={!eodData?.reports?.length}
              className="h-9 text-xs font-medium rounded-xl border flex items-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5 text-slate-500" />
              Export CSV
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchEodReports()}
              disabled={loadingEods}
              className="h-9 text-xs font-medium rounded-xl border"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingEods ? "animate-spin text-emerald-600" : "text-slate-500"}`} />
            </Button>
          </div>
        </div>

        {/* 4 EOD KPI Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
          <Card className={`rounded-2xl border ${isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800"}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium uppercase tracking-wider ${isLight ? "text-slate-500" : "text-zinc-400"}`}>Total Team</p>
                <p className={`text-2xl font-bold font-mono mt-1 ${isLight ? "text-slate-900" : "text-white"}`}>
                  {eodData?.total_members ?? stats.total}
                </p>
                <p className={`text-[11px] mt-0.5 ${isLight ? "text-slate-400" : "text-zinc-500"}`}>Active staff roster</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isLight ? "bg-slate-100 text-slate-700" : "bg-zinc-800 text-zinc-300"}`}>
                <Users className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>

          <Card className={`rounded-2xl border ${isLight ? "bg-white border-emerald-200/80 shadow-xs bg-gradient-to-br from-white to-emerald-50/20" : "bg-zinc-900/90 border-emerald-900/60"}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-emerald-700 dark:text-emerald-400">EODs Submitted</p>
                <p className="text-2xl font-bold font-mono mt-1 text-emerald-700 dark:text-emerald-400">
                  {eodData?.submitted_count ?? 0}
                </p>
                <p className="text-[11px] mt-0.5 text-emerald-600">
                  {eodData?.total_members ? Math.round(((eodData?.submitted_count || 0) / eodData.total_members) * 100) : 0}% Submission rate
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100 dark:bg-emerald-950/60 dark:border-emerald-900 flex items-center justify-center">
                <CheckCircle className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>

          <Card className={`rounded-2xl border ${isLight ? "bg-white border-amber-200/80 shadow-xs bg-gradient-to-br from-white to-amber-50/20" : "bg-zinc-900/90 border-amber-900/60"}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-amber-700 dark:text-amber-400">Pending EODs</p>
                <p className="text-2xl font-bold font-mono mt-1 text-amber-700 dark:text-amber-400">
                  {eodData?.pending_count ?? 0}
                </p>
                <p className="text-[11px] mt-0.5 text-amber-600">Awaiting report filing</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 border border-amber-100 dark:bg-amber-950/60 dark:border-amber-900 flex items-center justify-center">
                <Clock className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>

          <Card className={`rounded-2xl border ${isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800"}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className={`text-xs font-medium uppercase tracking-wider ${isLight ? "text-slate-500" : "text-zinc-400"}`}>Floor Presence</p>
                <p className={`text-2xl font-bold font-mono mt-1 ${isLight ? "text-slate-900" : "text-white"}`}>
                  {(eodData?.clocked_in_count || 0) + (eodData?.clocked_out_count || 0)} Punched
                </p>
                <p className={`text-[11px] mt-0.5 ${isLight ? "text-slate-400" : "text-zinc-500"}`}>
                  {eodData?.clocked_in_count || 0} active on floor now
                </p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isLight ? "bg-blue-50 text-blue-600" : "bg-blue-950/60 text-blue-400"}`}>
                <UserCheck className="w-5 h-5" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter Toolbar */}
        <div className={`p-4 rounded-2xl border flex flex-col md:flex-row md:items-center justify-between gap-3 ${
          isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800"
        }`}>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Status Tabs */}
            <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-zinc-800 rounded-xl">
              {[
                { id: "all", label: `All Members (${eodData?.total_members || 0})` },
                { id: "submitted", label: `Submitted (${eodData?.submitted_count || 0})` },
                { id: "pending", label: `Pending (${eodData?.pending_count || 0})` }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setEodStatusFilter(tab.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    eodStatusFilter === tab.id
                      ? "bg-white dark:bg-zinc-900 text-slate-900 dark:text-white shadow-xs font-semibold"
                      : "text-slate-600 dark:text-zinc-400 hover:text-slate-900"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Department Dropdown */}
            <select
              value={selectedEodDept}
              onChange={(e) => {
                setSelectedEodDept(e.target.value);
                fetchEodReports(eodDate, e.target.value);
              }}
              className={`text-xs px-3 py-2 rounded-xl border outline-none ${
                isLight ? "bg-white border-slate-200 text-slate-700" : "bg-zinc-800 border-zinc-700 text-zinc-300"
              }`}
            >
              <option value="All">All Departments</option>
              {departments.filter(d => d !== "All").map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div className="relative w-full md:w-64">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              placeholder="Search member name or title..."
              value={eodSearchQuery}
              onChange={(e) => setEodSearchQuery(e.target.value)}
              className="pl-8 text-xs h-9 rounded-xl"
            />
          </div>
        </div>

        {/* Reports Feed */}
        {loadingEods ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-6 h-6 animate-spin text-emerald-600 mx-auto mb-2" />
            <p className="text-xs text-slate-500">Loading daily EOD reports...</p>
          </div>
        ) : !eodData?.reports?.length ? (
          <div className="p-12 text-center bg-white dark:bg-zinc-900 rounded-2xl border border-slate-200 dark:border-zinc-800">
            <FileText className="w-10 h-10 text-slate-300 dark:text-zinc-600 mx-auto mb-3" />
            <h4 className="text-sm font-semibold text-slate-800 dark:text-zinc-200">No reports found</h4>
            <p className="text-xs text-slate-500 mt-1">Try selecting a different date or department filter.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {eodData.reports
              .filter(r => {
                if (eodStatusFilter === "submitted" && !r.eod_report?.has_submitted) return false;
                if (eodStatusFilter === "pending" && r.eod_report?.has_submitted) return false;
                if (eodSearchQuery.trim()) {
                  const q = eodSearchQuery.toLowerCase();
                  return (
                    r.name?.toLowerCase().includes(q) ||
                    r.email?.toLowerCase().includes(q) ||
                    r.team_title?.toLowerCase().includes(q) ||
                    r.department?.toLowerCase().includes(q)
                  );
                }
                return true;
              })
              .map((r) => {
                const hasSubmitted = Boolean(r.eod_report?.has_submitted);

                return (
                  <div
                    key={r.email}
                    className={`p-5 rounded-2xl border transition-all ${
                      hasSubmitted
                        ? isLight ? "bg-white border-slate-200/90 shadow-xs" : "bg-zinc-900 border-zinc-800"
                        : isLight ? "bg-amber-50/20 border-amber-200/60" : "bg-zinc-900/60 border-zinc-800/80"
                    }`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      {/* Member Info */}
                      <div className="flex items-start gap-3.5 min-w-0">
                        <UserAvatar
                          src={r.picture}
                          alt={r.name}
                          className="w-11 h-11 rounded-2xl border shrink-0"
                        />

                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className={`text-sm font-bold ${isLight ? "text-slate-900" : "text-white"}`}>
                              {r.name}
                            </h4>
                            <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400 font-medium">
                              {r.department}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {r.team_title} • <span className="font-mono">{r.email}</span>
                          </p>

                          {/* Shift Punch Row */}
                          <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
                            <span className="flex items-center gap-1 text-slate-600 dark:text-zinc-400 font-mono text-[11px]">
                              <Clock className="w-3.5 h-3.5 text-slate-400" />
                              In: <strong className="text-slate-800 dark:text-zinc-200">{r.attendance?.punch_in_time || "--:--"}</strong>
                            </span>
                            <span className="text-slate-300">•</span>
                            <span className="flex items-center gap-1 text-slate-600 dark:text-zinc-400 font-mono text-[11px]">
                              Out: <strong className="text-slate-800 dark:text-zinc-200">{r.attendance?.punch_out_time || "--:--"}</strong>
                            </span>
                            <span className="text-slate-300">•</span>
                            <span className="flex items-center gap-1 text-slate-600 dark:text-zinc-400 font-mono text-[11px]">
                              Duration: <strong className="text-slate-800 dark:text-zinc-200">{r.attendance?.duration_formatted || "--"}</strong>
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Submission Status Badge & Quick Action */}
                      <div className="flex items-center gap-2 self-start shrink-0">
                        {hasSubmitted ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs px-2.5 py-1 font-medium flex items-center gap-1">
                            <CheckCircle className="w-3.5 h-3.5" />
                            EOD Submitted
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs px-2.5 py-1 font-medium flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            Pending Report
                          </Badge>
                        )}

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const found = members.find(m => m.email === r.email);
                            if (found) handleSelectMember(found);
                          }}
                          className="text-xs h-8 px-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                        >
                          Dossier →
                        </Button>
                      </div>
                    </div>

                    {/* EOD Report Content Box */}
                    {hasSubmitted ? (
                      <div className={`mt-4 p-4 rounded-xl border space-y-2.5 ${
                        isLight ? "bg-slate-50/80 border-slate-200/80" : "bg-zinc-800/60 border-zinc-700/60"
                      }`}>
                        <div className="flex items-center justify-between text-xs border-b border-slate-200/60 dark:border-zinc-700/60 pb-2">
                          <span className="font-semibold text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5" /> Accomplishments & Daily Summary
                          </span>
                          {r.eod_report?.hours_worked && (
                            <span className="text-[11px] font-mono text-slate-500">
                              Hours Logged: <strong className="text-slate-800 dark:text-zinc-200">{r.eod_report.hours_worked}h</strong>
                            </span>
                          )}
                        </div>

                        <p className="text-xs text-slate-700 dark:text-zinc-300 whitespace-pre-wrap leading-relaxed font-sans">
                          {r.eod_report.summary}
                        </p>

                        {r.eod_report.blockers && (
                          <div className="p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/60 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                            <div>
                              <strong className="font-semibold">Blockers / Dependencies:</strong>{" "}
                              <span>{r.eod_report.blockers}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mt-3 p-3 rounded-xl bg-amber-50/60 dark:bg-zinc-800/40 border border-amber-200/50 dark:border-zinc-700 text-xs text-amber-800 dark:text-amber-400 flex items-center justify-between">
                        <span>No End-of-Day report filed yet for this date.</span>
                        <span className="text-[11px] font-mono font-medium text-slate-500">
                          Shift: {r.attendance?.status === "clocked_in" ? "🟢 Currently Active" : (r.attendance?.status === "clocked_out" ? "Left without EOD" : "Not Punched")}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </div>
    )}

    {/* ========================================================================= */}
    {/* ===================== TAB 3: COMPANY TASK HUB =========================== */}
    {/* ========================================================================= */}
    {mainTab === "tasks" && (
      <div className="space-y-6 animate-in fade-in duration-200">
        {/* Top KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Card className={`rounded-2xl border ${isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800"}`}>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-slate-500 uppercase">Total Tasks</p>
              <p className="text-2xl font-bold font-mono text-slate-900 dark:text-white mt-1">
                {taskStats?.total_count || companyTasks.length}
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5">All company deliverables</p>
            </CardContent>
          </Card>

          <Card className={`rounded-2xl border ${isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800"}`}>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-slate-500 uppercase">To Do</p>
              <p className="text-2xl font-bold font-mono text-slate-700 dark:text-zinc-300 mt-1">
                {taskStats?.todo_count || 0}
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5">Backlog / Queue</p>
            </CardContent>
          </Card>

          <Card className={`rounded-2xl border ${isLight ? "bg-white border-amber-200/80 shadow-xs bg-gradient-to-br from-white to-amber-50/20" : "bg-zinc-900/90 border-amber-900/60"}`}>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-amber-700 dark:text-amber-400 uppercase">In Progress</p>
              <p className="text-2xl font-bold font-mono text-amber-600 dark:text-amber-400 mt-1">
                {taskStats?.in_progress_count || 0}
              </p>
              <p className="text-[11px] text-amber-600 mt-0.5">Currently active</p>
            </CardContent>
          </Card>

          <Card className={`rounded-2xl border ${isLight ? "bg-white border-emerald-200/80 shadow-xs bg-gradient-to-br from-white to-emerald-50/20" : "bg-zinc-900/90 border-emerald-900/60"}`}>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-emerald-700 dark:text-emerald-400 uppercase">Completed</p>
              <p className="text-2xl font-bold font-mono text-emerald-600 dark:text-emerald-400 mt-1">
                {taskStats?.completed_count || 0}
              </p>
              <p className="text-[11px] text-emerald-600 mt-0.5">Shipped deliverables</p>
            </CardContent>
          </Card>

          <Card className={`rounded-2xl border ${isLight ? "bg-white border-red-200/80 shadow-xs bg-gradient-to-br from-white to-red-50/20" : "bg-zinc-900/90 border-red-900/60"}`}>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-red-700 dark:text-red-400 uppercase">P0 Urgent</p>
              <p className="text-2xl font-bold font-mono text-red-600 dark:text-red-400 mt-1">
                {taskStats?.p0_count || 0}
              </p>
              <p className="text-[11px] text-red-600 mt-0.5">Critical SLA targets</p>
            </CardContent>
          </Card>
        </div>

        {/* Task Filters & Create Action Bar */}
        <div className={`p-4 rounded-2xl border flex flex-col md:flex-row md:items-center justify-between gap-3 ${
          isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900/90 border-zinc-800"
        }`}>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Status Filter */}
            <select
              value={taskStatusFilter}
              onChange={(e) => {
                setTaskStatusFilter(e.target.value);
                fetchAllCompanyTasks();
              }}
              className={`text-xs px-3 py-2 rounded-xl border outline-none ${
                isLight ? "bg-white border-slate-200 text-slate-700" : "bg-zinc-800 border-zinc-700 text-zinc-300"
              }`}
            >
              <option value="all">All Statuses</option>
              <option value="todo">To Do</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>

            {/* Priority Filter */}
            <select
              value={taskPriorityFilter}
              onChange={(e) => {
                setTaskPriorityFilter(e.target.value);
                fetchAllCompanyTasks();
              }}
              className={`text-xs px-3 py-2 rounded-xl border outline-none ${
                isLight ? "bg-white border-slate-200 text-slate-700" : "bg-zinc-800 border-zinc-700 text-zinc-300"
              }`}
            >
              <option value="all">All Priorities</option>
              <option value="P0 (Urgent)">🔴 P0 Urgent</option>
              <option value="P1 (High)">🟠 P1 High</option>
              <option value="P2 (Normal)">🔵 P2 Normal</option>
            </select>

            {/* Assignee Filter */}
            <select
              value={taskAssigneeFilter}
              onChange={(e) => {
                setTaskAssigneeFilter(e.target.value);
                fetchAllCompanyTasks();
              }}
              className={`text-xs px-3 py-2 rounded-xl border outline-none ${
                isLight ? "bg-white border-slate-200 text-slate-700" : "bg-zinc-800 border-zinc-700 text-zinc-300"
              }`}
            >
              <option value="all">All Team Members</option>
              {members.map(m => (
                <option key={m.email} value={m.email}>{m.name}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative w-full md:w-56">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Search tasks..."
                value={taskSearchQuery}
                onChange={(e) => setTaskSearchQuery(e.target.value)}
                className="pl-8 text-xs h-9 rounded-xl"
              />
            </div>

            <Button
              size="sm"
              onClick={() => setShowAssignModal(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs h-9 px-3 rounded-xl shadow-xs shrink-0 flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              Assign Task
            </Button>
          </div>
        </div>

        {/* Task List */}
        {loadingTasks ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-6 h-6 animate-spin text-emerald-600 mx-auto mb-2" />
            <p className="text-xs text-slate-500">Loading company tasks...</p>
          </div>
        ) : !companyTasks.length ? (
          <div className="p-12 text-center bg-white dark:bg-zinc-900 rounded-2xl border border-slate-200 dark:border-zinc-800">
            <CheckSquare className="w-10 h-10 text-slate-300 dark:text-zinc-600 mx-auto mb-3" />
            <h4 className="text-sm font-semibold text-slate-800 dark:text-zinc-200">No tasks found</h4>
            <p className="text-xs text-slate-500 mt-1">Assign a new task using the button above.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {companyTasks
              .filter(t => {
                if (taskSearchQuery.trim()) {
                  const q = taskSearchQuery.toLowerCase();
                  return (
                    t.title?.toLowerCase().includes(q) ||
                    t.description?.toLowerCase().includes(q) ||
                    t.assigned_to_name?.toLowerCase().includes(q)
                  );
                }
                return true;
              })
              .map((task) => {
                const isDone = task.status === "completed" || task.status === "Done";
                const isUrgent = (task.priority || "").includes("P0");
                const checklist = task.checklist || [];

                return (
                  <div
                    key={task.task_id}
                    className={`p-5 rounded-2xl border flex flex-col justify-between transition-all ${
                      isDone
                        ? isLight ? "bg-slate-50/70 border-slate-200/60 opacity-80" : "bg-zinc-900/60 border-zinc-800/80 opacity-70"
                        : isLight ? "bg-white border-slate-200/90 shadow-xs hover:shadow-sm" : "bg-zinc-900 border-zinc-800"
                    }`}
                  >
                    <div className="space-y-3">
                      {/* Top Badges */}
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400">
                          {task.category || "Operations"}
                        </span>

                        <Badge className={`text-[10px] px-2 py-0.5 font-medium ${
                          isUrgent
                            ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/60 dark:text-red-400"
                            : (task.priority || "").includes("P1")
                            ? "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-400"
                            : "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/60 dark:text-blue-400"
                        }`}>
                          {task.priority || "P2 (Normal)"}
                        </Badge>
                      </div>

                      {/* Title & Desc */}
                      <div>
                        <h4 className={`text-sm font-bold text-slate-900 dark:text-white leading-snug ${isDone ? "line-through text-slate-400 dark:text-zinc-500" : ""}`}>
                          {task.title}
                        </h4>
                        {task.description && (
                          <p className="text-xs text-slate-500 dark:text-zinc-400 mt-1 line-clamp-2 leading-relaxed">
                            {task.description}
                          </p>
                        )}
                      </div>

                      {/* Checklist preview */}
                      {checklist.length > 0 && (
                        <div className="pt-2 border-t border-slate-100 dark:border-zinc-800 space-y-1">
                          {checklist.slice(0, 3).map((item, idx) => (
                            <div
                              key={item.id || idx}
                              onClick={() => handleToggleAdminChecklistItem(task, idx)}
                              className="flex items-center gap-2 text-xs text-slate-600 dark:text-zinc-400 hover:text-slate-900 cursor-pointer select-none py-0.5"
                            >
                              <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                                item.done ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300 dark:border-zinc-600"
                              }`}>
                                {item.done && <Check className="w-2.5 h-2.5 stroke-[3]" />}
                              </div>
                              <span className={`text-xs truncate ${item.done ? "line-through text-slate-400" : ""}`}>
                                {item.text}
                              </span>
                            </div>
                          ))}
                          {checklist.length > 3 && (
                            <p className="text-[10px] text-slate-400">+{checklist.length - 3} more items</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Bottom Meta & Actions */}
                    <div className="mt-4 pt-3 border-t border-slate-100 dark:border-zinc-800 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-semibold text-slate-800 dark:text-zinc-200 truncate">
                          👤 {task.assigned_to_name || "Staff"}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          onClick={() => handleToggleAdminTaskStatus(task.task_id || task.id || task._id, task.status)}
                          className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all ${
                            isDone
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : task.status === "in_progress"
                              ? "bg-amber-50 text-amber-700 border border-amber-200"
                              : "bg-slate-100 text-slate-700 border border-slate-200"
                          }`}
                        >
                          {isDone ? "Completed" : (task.status === "in_progress" ? "In Progress" : "To Do")}
                        </button>

                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteAdminTask(task.task_id || task.id || task._id)}
                          className="h-7 w-7 text-slate-400 hover:text-red-600 rounded-lg"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    )}

    {/* ========================================================================= */}
    {/* ===================== SPACIOUS EXECUTIVE DOSSIER MODAL ================= */}
    {/* ========================================================================= */}
        {selectedMember && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className={`relative w-full max-w-5xl h-[92vh] flex flex-col rounded-3xl border shadow-2xl overflow-hidden transition-all ${
              isLight ? "bg-white border-slate-200 text-slate-900" : "bg-zinc-900 border-zinc-800 text-white"
            }`}>
              
              {/* Dossier Header */}
              <div className={`p-6 border-b flex flex-col md:flex-row md:items-center justify-between gap-4 ${
                isLight ? "bg-slate-50/60 border-slate-200/80" : "bg-zinc-950/60 border-zinc-800"
              }`}>
                {/* Employee Profile Metadata */}
                <div className="flex items-center gap-4 min-w-0">
                  <div className="relative shrink-0 group">
                    <UserAvatar
                      src={selectedMember.picture}
                      alt={selectedMember.name}
                      className={`w-16 h-16 rounded-2xl border-2 shadow-sm ${
                        isLight ? "border-white" : "border-zinc-700"
                      }`}
                    />
                    <label
                      title="Upload custom photo"
                      className="absolute inset-0 bg-black/40 rounded-2xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    >
                      <Camera className="w-5 h-5 text-white" />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            handleUploadMemberPhoto(selectedMember.email, e.target.files[0]);
                          }
                        }}
                      />
                    </label>
                    {(() => {
                      const attStatus = selectedMember.attendance?.status || selectedMember.today?.status || memberDetail?.today?.status;
                      const punchIn = selectedMember.attendance?.punch_in_time || selectedMember.today?.punch_in_time || memberDetail?.today?.punch_in_time;
                      return (
                        <>
                          <span className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 ${
                            isLight ? "border-white" : "border-zinc-900"
                          } ${
                            attStatus === "clocked_in"
                              ? "bg-emerald-500"
                              : attStatus === "clocked_out"
                              ? "bg-amber-500"
                              : "bg-slate-400"
                          }`} />
                        </>
                      );
                    })()}
                  </div>

                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-xl md:text-2xl font-bold tracking-tight">
                        {selectedMember.name}
                      </h2>
                      {(() => {
                        const attStatus = selectedMember.attendance?.status || selectedMember.today?.status || memberDetail?.today?.status;
                        const punchIn = selectedMember.attendance?.punch_in_time || selectedMember.today?.punch_in_time || memberDetail?.today?.punch_in_time;
                        return (
                          <Badge variant="outline" className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                            attStatus === "clocked_in"
                              ? isLight ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-emerald-950/60 text-emerald-400 border-emerald-800"
                              : attStatus === "clocked_out"
                              ? isLight ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-amber-950/60 text-amber-400 border-amber-800"
                              : isLight ? "bg-slate-100 text-slate-600 border-slate-200" : "bg-zinc-800 text-zinc-400 border-zinc-700"
                          }`}>
                            {attStatus === "clocked_in"
                              ? `Clocked In On-Site ${punchIn ? `(${punchIn})` : ""}`
                              : attStatus === "clocked_out"
                              ? "Checked Out Today"
                              : "Not Punched Today"}
                          </Badge>
                        );
                      })()}
                      <Badge variant="outline" className={`text-xs ${
                        isLight ? "text-slate-700 border-slate-300 bg-slate-50" : "text-zinc-300 border-zinc-700 bg-zinc-800"
                      }`}>
                        {resolveMemberDepartment(selectedMember)}
                      </Badge>
                    </div>

                    <p className={`text-xs md:text-sm mt-0.5 font-medium ${
                      isLight ? "text-slate-600" : "text-zinc-300"
                    }`}>
                      {selectedMember.team_title || "Team Member"} • {selectedMember.role || "team_member"}
                    </p>

                    <div className={`flex items-center gap-4 text-xs mt-1.5 flex-wrap ${
                      isLight ? "text-slate-500" : "text-zinc-400"
                    }`}>
                      <span className="flex items-center gap-1 font-mono">
                        <Mail className="w-3.5 h-3.5 text-slate-400" /> {selectedMember.email}
                      </span>
                      {selectedMember.phone && (
                        <span className="flex items-center gap-1 font-mono">
                          <Phone className="w-3.5 h-3.5 text-slate-400" /> {selectedMember.phone}
                        </span>
                      )}
                      {selectedMember.escalate_to && selectedMember.escalate_to !== "-" && (
                        <span className={`text-[11px] px-2 py-0.5 rounded ${
                          isLight ? "bg-blue-50 text-blue-700 border border-blue-100" : "bg-blue-950/40 text-blue-400 border border-blue-900"
                        }`}>
                          Reporting To: {selectedMember.escalate_to}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Dossier Top Actions (Clean Contextual Hierarchy) */}
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                  <Button
                    size="sm"
                    onClick={() => setShowAssignModal(true)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs h-8 px-3 rounded-xl shadow-xs"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Assign Task
                  </Button>

                  {/* Reports Quick Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={loadingDownload}
                        className={`text-xs h-8 rounded-xl border transition-all ${
                          isLight 
                            ? "border-slate-300 text-slate-800 bg-white hover:bg-slate-100 hover:text-slate-900 shadow-xs" 
                            : "border-zinc-700 text-zinc-200 bg-zinc-800 hover:bg-zinc-700 hover:text-white"
                        }`}
                      >
                        <Download className="w-3.5 h-3.5 mr-1.5 text-slate-500 dark:text-slate-400" />
                        Download Report
                        <ChevronDown className="w-3.5 h-3.5 ml-1 text-slate-500 dark:text-slate-400" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 text-xs bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 shadow-lg">
                      <DropdownMenuItem onClick={() => handleAdminDownloadReport(selectedMember.email, 15)} className="text-slate-700 dark:text-zinc-200 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-zinc-800 cursor-pointer">
                        <FileText className="w-3.5 h-3.5 mr-2 text-slate-500" /> 15-Day Performance
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleAdminDownloadReport(selectedMember.email, 30)} className="text-slate-700 dark:text-zinc-200 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-zinc-800 cursor-pointer">
                        <FileText className="w-3.5 h-3.5 mr-2 text-slate-500" /> 30-Day Monthly Ledger
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* More Actions Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className={`h-8 w-8 rounded-xl border transition-all ${
                          isLight 
                            ? "border-slate-300 text-slate-700 bg-white hover:bg-slate-100 hover:text-slate-900 shadow-xs" 
                            : "border-zinc-700 text-zinc-300 bg-zinc-800 hover:bg-zinc-700 hover:text-white"
                        }`}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52 text-xs bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 shadow-lg">
                      <DropdownMenuItem onClick={(e) => handleOpenEditMember(selectedMember, e)} className="text-slate-700 dark:text-zinc-200 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-zinc-800 cursor-pointer">
                        <KeyRound className="w-3.5 h-3.5 mr-2 text-blue-600" />
                        Edit Profile & Reset Password
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setMemberToDelete(selectedMember)}
                        className="text-rose-600 focus:text-rose-600 focus:bg-rose-50 cursor-pointer"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-2" />
                        Remove Member from Roster
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Close Modal Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSelectedMember(null);
                      setMemberDetail(null);
                    }}
                    className="rounded-full h-8 w-8 text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-white dark:hover:bg-zinc-800 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* Dossier High-Level Navigation Tabs */}
              <div className={`px-6 pt-2 border-b flex items-center gap-1 overflow-x-auto custom-scrollbar shrink-0 ${
                isLight ? "bg-slate-50/40 border-slate-200/80" : "bg-zinc-950/40 border-zinc-800"
              }`}>
                {[
                  { id: "overview", label: "Overview & Verification", icon: ShieldCheck },
                  { id: "attendance", label: "Attendance Audit Ledger", icon: Clock },
                  { id: "tasks", label: `Tasks & Deliverables (${memberDetail?.tasks?.length || 0})`, icon: CheckSquare },
                  { id: "reports", label: `Daily EOD Reports (${memberDetail?.daily_reports?.length || 0})`, icon: FileText },
                  { id: "kpis", label: "KPI Mandates & Scorecard", icon: Target },
                ].map((tab) => {
                  const Icon = tab.icon;
                  const isActive = detailTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setDetailTab(tab.id)}
                      className={`pb-3 pt-2 px-3 text-xs md:text-sm font-semibold border-b-2 whitespace-nowrap transition-all flex items-center gap-2 ${
                        isActive
                          ? isLight
                            ? "border-emerald-600 text-emerald-700 font-bold"
                            : "border-emerald-500 text-emerald-400 font-bold"
                          : "border-transparent text-slate-500 hover:text-slate-900"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* Dossier Content Body */}
              <div className="flex-1 overflow-y-auto p-6 custom-scrollbar space-y-6">
                {loadingDetail ? (
                  <div className="py-24 text-center">
                    <RefreshCw className="w-7 h-7 animate-spin mx-auto text-emerald-600 mb-3" />
                    <p className={`text-sm ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                      Loading {selectedMember.name}'s attendance logs and task records...
                    </p>
                  </div>
                ) : (
                  <>
                    {/* ===================== TAB 1: OVERVIEW & VERIFICATION ===================== */}
                    {detailTab === "overview" && (
                      <div className="space-y-6">
                        
                        {/* Today's Verification Hero Card */}
                        <div className={`p-5 rounded-2xl border ${
                          isLight ? "bg-slate-50/60 border-slate-200/80" : "bg-zinc-950/60 border-zinc-800"
                        }`}>
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b pb-3 mb-4 border-slate-200/60">
                            <div>
                              <span className="text-xs font-mono uppercase tracking-wider text-emerald-700 font-semibold">
                                Live Shift Audit • {memberDetail?.today?.display_date || "Today"}
                              </span>
                              <h3 className={`text-base md:text-lg font-bold mt-0.5 ${
                                isLight ? "text-slate-900" : "text-white"
                              }`}>
                                Today's On-Site Biometric & GPS Verification
                              </h3>
                            </div>

                            <Badge
                              variant="outline"
                              className={`text-xs px-3 py-1 font-mono font-medium rounded-full ${
                                memberDetail?.today?.is_clocked_in
                                  ? isLight ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-emerald-950/60 text-emerald-400 border-emerald-800"
                                  : memberDetail?.today?.punch_out_time
                                  ? isLight ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-amber-950/60 text-amber-400 border-amber-800"
                                  : isLight ? "bg-slate-100 text-slate-600 border-slate-200" : "bg-zinc-800 text-zinc-400 border-zinc-700"
                              }`}
                            >
                              {memberDetail?.today?.is_clocked_in
                                ? "Clocked In (Active On-Site)"
                                : memberDetail?.today?.punch_out_time
                                ? "Clocked Out (Shift Completed)"
                                : "Not Punched Yet Today"}
                            </Badge>
                          </div>

                          {/* 3-Column Verification Grid */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            
                            {/* Check-In Card */}
                            <div className={`p-4 rounded-xl border flex flex-col justify-between ${
                              isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900 border-zinc-800"
                            }`}>
                              <div>
                                <p className="text-xs font-medium text-slate-500 flex items-center gap-1.5">
                                  <Clock className="w-3.5 h-3.5 text-emerald-600" />
                                  Check-In Time (IST)
                                </p>
                                <p className={`text-2xl font-bold font-mono mt-1 ${
                                  isLight ? "text-slate-900" : "text-white"
                                }`}>
                                  {memberDetail?.today?.punch_in_time || "—"}
                                </p>
                                <p className="text-[11px] text-emerald-600 font-medium mt-1 flex items-center gap-1">
                                  <ShieldCheck className="w-3.5 h-3.5" />
                                  {memberDetail?.today?.punch_in_time ? "Biometric & Geofence Verified" : "Pending punch action"}
                                </p>
                              </div>

                              {memberDetail?.today?.punch_in_selfie && (
                                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-3">
                                  <img
                                    src={memberDetail.today.punch_in_selfie}
                                    alt="Punch In Selfie"
                                    onClick={() => setInspectedImage(memberDetail.today.punch_in_selfie)}
                                    className="w-12 h-12 rounded-xl object-cover border border-emerald-200 cursor-pointer hover:opacity-85 transition-opacity shadow-xs"
                                  />
                                  <div className="text-[11px]">
                                    <span className="font-semibold text-emerald-700 block">Check-In Photo</span>
                                    <button
                                      type="button"
                                      onClick={() => setInspectedImage(memberDetail.today.punch_in_selfie)}
                                      className="text-[10px] text-slate-500 hover:text-slate-900 underline"
                                    >
                                      Click to zoom photo
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Check-Out Card */}
                            <div className={`p-4 rounded-xl border flex flex-col justify-between ${
                              isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900 border-zinc-800"
                            }`}>
                              <div>
                                <p className="text-xs font-medium text-slate-500 flex items-center gap-1.5">
                                  <Clock className="w-3.5 h-3.5 text-amber-500" />
                                  Check-Out Time (IST)
                                </p>
                                <p className={`text-2xl font-bold font-mono mt-1 ${
                                  isLight ? "text-slate-900" : "text-white"
                                }`}>
                                  {memberDetail?.today?.punch_out_time || (memberDetail?.today?.is_clocked_in ? "In Shift" : "—")}
                                </p>
                                <p className="text-[11px] text-slate-500 mt-1">
                                  Total Worked: <strong className="font-mono text-slate-800">{memberDetail?.today?.duration_formatted || "—"}</strong>
                                </p>
                              </div>

                              {memberDetail?.today?.punch_out_selfie && (
                                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-3">
                                  <img
                                    src={memberDetail.today.punch_out_selfie}
                                    alt="Punch Out Selfie"
                                    onClick={() => setInspectedImage(memberDetail.today.punch_out_selfie)}
                                    className="w-12 h-12 rounded-xl object-cover border border-amber-200 cursor-pointer hover:opacity-85 transition-opacity shadow-xs"
                                  />
                                  <div className="text-[11px]">
                                    <span className="font-semibold text-amber-700 block">Check-Out Photo</span>
                                    <button
                                      type="button"
                                      onClick={() => setInspectedImage(memberDetail.today.punch_out_selfie)}
                                      className="text-[10px] text-slate-500 hover:text-slate-900 underline"
                                    >
                                      Click to zoom photo
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Campus GPS Geofence */}
                            <div className={`p-4 rounded-xl border flex flex-col justify-between ${
                              isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900 border-zinc-800"
                            }`}>
                              <div>
                                <p className="text-xs font-medium text-slate-500 flex items-center gap-1.5">
                                  <MapPin className="w-3.5 h-3.5 text-blue-600" />
                                  Campus Geofence Audit
                                </p>
                                <p className={`text-xs font-semibold mt-1 leading-snug ${
                                  isLight ? "text-slate-800" : "text-zinc-200"
                                }`}>
                                  {memberDetail?.today?.location?.location_name || "JamaDhan Flagship Academy, Jaipur"}
                                </p>
                                {memberDetail?.today?.location?.latitude && (
                                  <p className="text-[11px] text-slate-400 mt-1 font-mono">
                                    Lat: {memberDetail.today.location.latitude.toFixed(4)} • Lon: {memberDetail.today.location.longitude.toFixed(4)}
                                  </p>
                                )}
                              </div>

                              {memberDetail?.today?.location?.latitude ? (
                                <a
                                  href={`https://www.google.com/maps?q=${memberDetail.today.location.latitude},${memberDetail.today.location.longitude}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-[11px] text-emerald-700 hover:underline flex items-center gap-1 mt-2 pt-2 border-t border-slate-100 font-medium"
                                >
                                  <ExternalLink className="w-3 h-3" /> View GPS Location on Google Maps
                                </a>
                              ) : (
                                <div className="text-[11px] text-slate-400 mt-2 pt-2 border-t border-slate-100">
                                  Geofence Radius: 500m Authorized
                                </div>
                              )}
                            </div>

                          </div>

                          {/* Today's EOD Report Preview (if available) */}
                          {memberDetail?.today?.eod_report && (
                            <div className={`mt-4 p-4 rounded-xl border ${
                              isLight ? "bg-white border-emerald-200/80" : "bg-zinc-900 border-emerald-900"
                            }`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold text-emerald-700 flex items-center gap-1.5">
                                  <FileText className="w-3.5 h-3.5" /> Today's Submitted Daily Work Report (EOD)
                                </span>
                                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                                  Submitted Today
                                </Badge>
                              </div>
                              <p className={`text-xs whitespace-pre-wrap leading-relaxed p-3 rounded-lg border ${
                                isLight ? "bg-slate-50 border-slate-100 text-slate-800" : "bg-zinc-950 border-zinc-800 text-zinc-200"
                              }`}>
                                {memberDetail.today.eod_report}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Working Hours & Performance Executive Dashboard Strip */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
                          {/* Standard Working Hours Card */}
                          <div className={`p-4 rounded-2xl border transition-all ${
                            isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900 border-zinc-800"
                          }`}>
                            <div className="flex items-center justify-between">
                              <p className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Official Shift</p>
                              <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            </div>
                            <p className="text-lg md:text-xl font-bold font-mono text-slate-900 mt-1">
                              9:00 AM – 5:30 PM
                            </p>
                            <p className="text-[11px] text-emerald-600 font-medium mt-0.5">
                              8.5 Hours Standard Shift
                            </p>
                          </div>

                          {/* Weekly Average Working Hours */}
                          <div className={`p-4 rounded-2xl border transition-all ${
                            isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900 border-zinc-800"
                          }`}>
                            <p className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Weekly Avg Hours</p>
                            <p className="text-xl md:text-2xl font-bold font-mono text-emerald-700 mt-1">
                              {memberDetail?.performance_stats?.avg_weekly_hours || "8.5h / day"}
                            </p>
                            <p className="text-[11px] text-slate-500 mt-0.5">
                              Total: <strong className="font-mono text-slate-800">{memberDetail?.performance_stats?.total_weekly_hours || "42.5h"}</strong> this week
                            </p>
                          </div>

                          {/* Monthly Average Working Hours */}
                          <div className={`p-4 rounded-2xl border transition-all ${
                            isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900 border-zinc-800"
                          }`}>
                            <p className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Monthly Avg Hours</p>
                            <p className="text-xl md:text-2xl font-bold font-mono text-blue-700 mt-1">
                              {memberDetail?.performance_stats?.avg_monthly_hours || "8.4h / day"}
                            </p>
                            <p className="text-[11px] text-slate-500 mt-0.5">
                              Total: <strong className="font-mono text-slate-800">{memberDetail?.performance_stats?.total_monthly_hours || "204h"}</strong> logged
                            </p>
                          </div>

                          {/* 40-Day Attendance & Task Rate */}
                          <div className={`p-4 rounded-2xl border transition-all ${
                            isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900 border-zinc-800"
                          }`}>
                            <p className="text-[11px] uppercase font-bold tracking-wider text-slate-500">40-Day Attendance Rate</p>
                            <p className="text-xl md:text-2xl font-bold font-mono text-amber-600 mt-1">
                              {memberDetail?.performance_stats?.attendance_rate || 96}%
                            </p>
                            <p className="text-[11px] text-slate-500 mt-0.5">
                              Task SLA: <strong className="font-mono text-emerald-700">{memberDetail?.performance_stats?.task_completion_rate || 100}%</strong>
                            </p>
                          </div>
                        </div>

                        {/* Recent Tasks Preview */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
                              <CheckSquare className="w-4 h-4 text-emerald-600" />
                              Active Deliverables Preview
                            </h4>
                            <button
                              onClick={() => setDetailTab("tasks")}
                              className="text-xs text-emerald-700 hover:underline font-semibold"
                            >
                              View All Tasks ({memberDetail?.tasks?.length || 0}) →
                            </button>
                          </div>

                          {(memberDetail?.tasks || []).length === 0 ? (
                            <div className={`p-6 text-center rounded-2xl border ${
                              isLight ? "bg-slate-50/50 border-slate-200/60" : "bg-zinc-950/40 border-zinc-800"
                            }`}>
                              <p className="text-xs text-slate-500">No tasks currently assigned to this member.</p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {(memberDetail.tasks || []).slice(0, 2).map((task) => (
                                <div
                                  key={task.task_id}
                                  className={`p-4 rounded-xl border ${
                                    isLight ? "bg-white border-slate-200/80" : "bg-zinc-900 border-zinc-800"
                                  }`}
                                >
                                  <div className="flex items-center justify-between gap-2 mb-1.5">
                                    <Badge variant="outline" className="text-[10px] font-mono">
                                      {task.priority}
                                    </Badge>
                                    <span className="text-[11px] font-mono text-slate-400">
                                      Due {task.timeline_date}
                                    </span>
                                  </div>
                                  <h5 className="font-semibold text-xs text-slate-900 line-clamp-1">
                                    {task.title}
                                  </h5>
                                  <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500 font-mono">
                                    <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                      <div
                                        className="h-full bg-emerald-600 rounded-full"
                                        style={{ width: `${task.progress_percent || 0}%` }}
                                      />
                                    </div>
                                    <span>{task.progress_percent || 0}%</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                      </div>
                    )}

                    {/* ===================== TAB 2: ATTENDANCE AUDIT LEDGER ===================== */}
                    {detailTab === "attendance" && (
                      <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div>
                            <h4 className="text-sm font-bold uppercase tracking-wider text-slate-800 flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-emerald-600" />
                              Past 40 Days Continuous Attendance Ledger
                            </h4>
                            <p className="text-xs text-slate-500 mt-0.5">
                              Certified on-site punches, timestamps, and worked durations for {selectedMember.name}.
                            </p>
                          </div>
                          <span className="text-xs font-mono text-slate-600">
                            40-Day Attendance Rate: <strong className="text-emerald-700">{memberDetail?.performance_stats?.attendance_rate || 96}%</strong>
                          </span>
                        </div>

                        <div className={`border rounded-2xl overflow-hidden ${
                          isLight ? "bg-white border-slate-200/80" : "bg-zinc-900 border-zinc-800"
                        }`}>
                          <div className="overflow-x-auto">
                            <table className="w-full text-left text-xs">
                              <thead className={`border-b text-[11px] font-semibold uppercase tracking-wider ${
                                isLight ? "bg-slate-50 text-slate-500 border-slate-200" : "bg-zinc-800 text-zinc-400 border-zinc-700"
                              }`}>
                                <tr>
                                  <th className="p-3">Date</th>
                                  <th className="p-3">Check-In (IST)</th>
                                  <th className="p-3">Check-Out (IST)</th>
                                  <th className="p-3">Total Duration</th>
                                  <th className="p-3">Work Mode</th>
                                  <th className="p-3">Campus Location</th>
                                  <th className="p-3">Audit Status</th>
                                </tr>
                              </thead>
                              <tbody className={`divide-y ${isLight ? "divide-slate-100" : "divide-zinc-800"}`}>
                                {(memberDetail?.attendance_history || []).map((log, idx) => (
                                  <tr key={log.date || idx} className={`transition-colors ${
                                    isLight ? "hover:bg-slate-50/60" : "hover:bg-zinc-800/40"
                                  }`}>
                                    <td className="p-3 font-mono font-medium text-slate-900 whitespace-nowrap">
                                      {log.display_date} ({log.day_name})
                                    </td>
                                    <td className="p-3 font-mono font-bold text-emerald-700">
                                      {log.punch_in_time}
                                    </td>
                                    <td className="p-3 font-mono text-slate-700">
                                      {log.punch_out_time}
                                    </td>
                                    <td className="p-3 font-mono text-slate-700">
                                      {log.duration_formatted}
                                    </td>
                                    <td className="p-3">
                                      <span className={`capitalize text-[11px] px-2 py-0.5 rounded font-medium ${
                                        isLight ? "bg-slate-100 text-slate-700" : "bg-zinc-800 text-zinc-300"
                                      }`}>
                                        {log.work_mode || "onsite"}
                                      </span>
                                    </td>
                                    <td className="p-3 text-slate-500 text-[11px] truncate max-w-[150px]">
                                      {log.location_name?.split("(")[0]?.trim() || "Jaipur Campus"}
                                    </td>
                                    <td className="p-3">
                                      <Badge
                                        variant="outline"
                                        className={`text-[10px] font-medium uppercase font-mono ${
                                          log.status === "clocked_in"
                                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                            : log.status === "present"
                                            ? "bg-blue-50 text-blue-700 border-blue-200"
                                            : log.status === "weekend_off"
                                            ? "bg-slate-100 text-slate-600 border-slate-200"
                                            : "bg-amber-50 text-amber-700 border-amber-200"
                                        }`}
                                      >
                                        {log.status === "clocked_in" ? "Present (In)" : log.status === "present" ? "Completed Shift" : log.status === "weekend_off" ? "Off" : log.status}
                                      </Badge>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ===================== TAB 3: TASKS & DELIVERABLES ===================== */}
                    {detailTab === "tasks" && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-sm font-bold uppercase tracking-wider text-slate-800">
                              Assigned Deliverables & Operational Tasks
                            </h4>
                            <p className="text-xs text-slate-500">
                              Track SLA adherence, checklist completion, and deadlines for {selectedMember.name}.
                            </p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => setShowAssignModal(true)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs h-8 rounded-xl"
                          >
                            <Plus className="w-3.5 h-3.5 mr-1" /> Add Task
                          </Button>
                        </div>

                        {(memberDetail?.tasks || []).length === 0 ? (
                          <div className={`p-10 text-center rounded-2xl border ${
                            isLight ? "bg-slate-50/50 border-slate-200/70" : "bg-zinc-950/40 border-zinc-800"
                          }`}>
                            <CheckSquare className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                            <p className="text-sm font-semibold text-slate-800">No tasks assigned</p>
                            <p className="text-xs text-slate-500 mt-1">Assign an operational SLA task to track progress</p>
                            <Button
                              size="sm"
                              onClick={() => setShowAssignModal(true)}
                              className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                            >
                              + Assign First Task
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {memberDetail.tasks.map((task) => {
                              const isCompleted = task.status === "completed" || task.status === "Done";
                              return (
                                <div
                                  key={task.task_id}
                                  className={`p-4 rounded-2xl border transition-all ${
                                    isCompleted
                                      ? isLight ? "bg-slate-50/70 border-slate-200/60 opacity-80" : "bg-zinc-950/60 border-zinc-800 opacity-80"
                                      : isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900 border-zinc-800"
                                  }`}
                                >
                                  <div className="flex items-start justify-between gap-3 mb-2">
                                    <div>
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <Badge
                                          className={`text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full ${
                                            task.priority?.includes("P0") || task.priority === "high"
                                              ? "bg-rose-50 text-rose-700 border-rose-200"
                                              : task.priority?.includes("P1")
                                              ? "bg-amber-50 text-amber-700 border-amber-200"
                                              : "bg-blue-50 text-blue-700 border-blue-200"
                                          }`}
                                        >
                                          {task.priority}
                                        </Badge>
                                        <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                                          {task.category || "Operations"}
                                        </span>
                                        <span className="text-xs font-mono text-slate-500">
                                          {task.timeline_label || `Due ${task.timeline_date}`}
                                        </span>
                                      </div>
                                      <h5 className="font-semibold text-sm md:text-base text-slate-900 mt-1.5">
                                        {task.title}
                                      </h5>
                                      {task.description && (
                                        <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                                          {task.description}
                                        </p>
                                      )}
                                    </div>

                                    <Badge
                                      variant="outline"
                                      className={`text-xs font-mono capitalize ${
                                        isCompleted
                                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                          : "bg-amber-50 text-amber-700 border-amber-200"
                                      }`}
                                    >
                                      {task.status}
                                    </Badge>
                                  </div>

                                  {/* Progress bar */}
                                  <div className="my-2.5">
                                    <div className="flex justify-between text-[11px] text-slate-500 mb-1 font-mono">
                                      <span>Checklist Completion</span>
                                      <span className="font-bold text-slate-800">{task.progress_percent || 0}%</span>
                                    </div>
                                    <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                      <div
                                        className={`h-full rounded-full transition-all duration-300 ${
                                          isCompleted ? "bg-emerald-600" : "bg-amber-500"
                                        }`}
                                        style={{ width: `${task.progress_percent || 0}%` }}
                                      />
                                    </div>
                                  </div>

                                  {/* Interactive Checklist items */}
                                  {task.checklist && task.checklist.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
                                      <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                                        Subtask Checklist ({task.checklist.filter(c => c.done).length}/{task.checklist.length}):
                                      </p>
                                      {task.checklist.map((item, cIdx) => (
                                        <div
                                          key={item.id || cIdx}
                                          onClick={() => handleToggleChecklist(task.task_id, task.checklist, cIdx)}
                                          className="flex items-center gap-2 text-xs text-slate-700 hover:text-slate-900 cursor-pointer select-none group"
                                        >
                                          {item.done ? (
                                            <CheckSquare className="w-4 h-4 text-emerald-600 shrink-0" />
                                          ) : (
                                            <Square className="w-4 h-4 text-slate-400 group-hover:text-slate-600 shrink-0" />
                                          )}
                                          <span className={item.done ? "line-through text-slate-400" : ""}>
                                            {item.text}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  <div className="flex items-center justify-between text-[11px] text-slate-400 mt-2.5 pt-2 border-t border-slate-100">
                                    <span>Assigned by: <strong>{task.assigned_by_name || "Admin"}</strong></span>
                                    <span className="font-mono">ID: {task.task_id}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* ===================== TAB 4: DAILY EOD REPORTS ===================== */}
                    {detailTab === "reports" && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-sm font-bold uppercase tracking-wider text-slate-800 flex items-center gap-2">
                              <FileText className="w-4 h-4 text-emerald-600" />
                              Daily Work Reports (EOD Submissions)
                            </h4>
                            <p className="text-xs text-slate-500">
                              Chronological archive of end-of-day reports submitted by {selectedMember.name}.
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAdminDownloadReport(selectedMember.email, 15)}
                              className="text-xs h-8"
                            >
                              <Download className="w-3.5 h-3.5 mr-1" /> 15-Day PDF
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAdminDownloadReport(selectedMember.email, 30)}
                              className="text-xs h-8"
                            >
                              <Download className="w-3.5 h-3.5 mr-1" /> Monthly PDF
                            </Button>
                          </div>
                        </div>

                        {(memberDetail?.daily_reports || []).length === 0 ? (
                          <div className={`p-10 text-center rounded-2xl border ${
                            isLight ? "bg-slate-50/50 border-slate-200/70" : "bg-zinc-950/40 border-zinc-800"
                          }`}>
                            <FileText className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                            <p className="text-sm font-semibold text-slate-800">No daily reports recorded yet</p>
                            <p className="text-xs text-slate-500 mt-1">
                              Reports submitted during punch-out from the workstation will appear here.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {memberDetail.daily_reports.map((rpt, idx) => (
                              <div
                                key={rpt.date || idx}
                                className={`p-4 rounded-2xl border space-y-2.5 ${
                                  isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900 border-zinc-800"
                                }`}
                              >
                                <div className="flex items-center justify-between border-b pb-2 border-slate-100">
                                  <div className="flex items-center gap-2">
                                    <Calendar className="w-3.5 h-3.5 text-emerald-600" />
                                    <span className="text-xs font-mono font-bold text-slate-900">
                                      {rpt.display_date || rpt.date}
                                    </span>
                                  </div>
                                  <span className="text-[11px] font-mono text-slate-400">
                                    Submitted at {rpt.submitted_at || "—"}
                                  </span>
                                </div>
                                <div className={`p-3 rounded-xl border ${
                                  isLight ? "bg-slate-50/70 border-slate-100" : "bg-zinc-950 border-zinc-800"
                                }`}>
                                  <p className="text-xs text-slate-800 whitespace-pre-wrap leading-relaxed">
                                    {rpt.report_text}
                                  </p>
                                </div>
                                {rpt.tasks_completed_count !== undefined && (
                                  <div className="text-[11px] text-slate-500 font-mono">
                                    Tasks Completed: <strong className="text-emerald-700">{rpt.tasks_completed_count}</strong>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* ===================== TAB 5: KPI MANDATES & SCORECARD ===================== */}
                    {detailTab === "kpis" && (
                      <div className="space-y-4">
                        <div className={`p-5 rounded-2xl border space-y-4 ${
                          isLight ? "bg-white border-slate-200/80 shadow-xs" : "bg-zinc-900 border-zinc-800"
                        }`}>
                          <h4 className="text-sm font-bold uppercase tracking-wider text-slate-800 flex items-center gap-2">
                            <Target className="w-4 h-4 text-emerald-600" />
                            Official Role Mandate & Core Performance KPIs
                          </h4>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className={`p-4 rounded-xl border ${
                              isLight ? "bg-slate-50/70 border-slate-100" : "bg-zinc-950 border-zinc-800"
                            }`}>
                              <p className="text-xs uppercase font-mono text-slate-500 font-semibold">Primary Role</p>
                              <p className="text-sm font-bold text-slate-900 mt-1">{selectedMember.team_title}</p>
                              <p className="text-xs text-slate-500 mt-0.5">{resolveMemberDepartment(selectedMember)}</p>
                            </div>

                            <div className={`p-4 rounded-xl border ${
                              isLight ? "bg-slate-50/70 border-slate-100" : "bg-zinc-950 border-zinc-800"
                            }`}>
                              <p className="text-xs uppercase font-mono text-emerald-700 font-semibold">Core KPIs</p>
                              <p className="text-sm font-medium text-slate-800 mt-1 leading-snug">{selectedMember.core_kpis}</p>
                            </div>

                            <div className={`p-4 rounded-xl border ${
                              isLight ? "bg-slate-50/70 border-slate-100" : "bg-zinc-950 border-zinc-800"
                            }`}>
                              <p className="text-xs uppercase font-mono text-amber-700 font-semibold">Daily Must-Do</p>
                              <p className="text-sm text-slate-700 mt-1 leading-snug">{selectedMember.daily_must_do}</p>
                            </div>

                            <div className={`p-4 rounded-xl border ${
                              isLight ? "bg-slate-50/70 border-slate-100" : "bg-zinc-950 border-zinc-800"
                            }`}>
                              <p className="text-xs uppercase font-mono text-blue-700 font-semibold">Weekly Must-Do</p>
                              <p className="text-sm text-slate-700 mt-1 leading-snug">{selectedMember.weekly_must_do}</p>
                            </div>
                          </div>

                          <div className={`p-3.5 rounded-xl border flex items-center justify-between text-xs ${
                            isLight ? "bg-blue-50/50 border-blue-100 text-blue-800" : "bg-blue-950/20 border-blue-900 text-blue-300"
                          }`}>
                            <span>
                              Official Escalation Authority: <strong>{selectedMember.escalate_to || "Deepak Sethia"}</strong>
                            </span>
                            <span className="font-mono text-slate-500">Compliance Protocol Active</span>
                          </div>
                        </div>
                      </div>
                    )}

                  </>
                )}
              </div>

              {/* Dossier Modal Footer */}
              <div className={`p-4 border-t flex items-center justify-between text-xs shrink-0 ${
                isLight ? "bg-slate-50/80 border-slate-200/80 text-slate-500" : "bg-zinc-950 border-zinc-800 text-zinc-400"
              }`}>
                <span className="font-mono">
                  Tagore Nagar Flagship Campus • Verified Enterprise Audit Record
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedMember(null);
                    setMemberDetail(null);
                  }}
                  className="text-xs h-8 rounded-xl"
                >
                  Close Dossier
                </Button>
              </div>

            </div>
          </div>
        )}

        {/* ===================== ASSIGN TASK MODAL ===================== */}
        {showAssignModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-150">
            <div className={`relative w-full max-w-lg rounded-3xl border shadow-2xl p-6 ${
              isLight ? "bg-white border-slate-200 text-slate-900" : "bg-zinc-900 border-zinc-800 text-white"
            }`}>
              <div className="flex items-center justify-between border-b pb-4 mb-4 border-slate-100">
                <div>
                  <h3 className="text-lg font-bold">Assign Operational Task</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Assign deliverables, SLAs, and interactive checklist targets
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowAssignModal(false)}
                  className="rounded-full h-8 w-8 text-slate-400 hover:text-slate-700"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <form onSubmit={handleAssignTask} className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Assign To Team Member *</Label>
                  <select
                    value={taskForm.assignee_email || selectedMember?.email || (members[0]?.email || "")}
                    onChange={(e) => {
                      const found = members.find(m => m.email === e.target.value);
                      if (found) setSelectedMember(found);
                      setTaskForm({ ...taskForm, assignee_email: e.target.value });
                    }}
                    className={`mt-1 w-full border rounded-xl px-2.5 py-2 text-xs outline-none ${
                      isLight ? "bg-white border-slate-200" : "bg-zinc-800 border-zinc-700 text-white"
                    }`}
                  >
                    {members.map(m => (
                      <option key={m.email} value={m.email}>
                        {m.name} — {m.team_title} ({resolveMemberDepartment(m)})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Task Title *</Label>
                  <Input
                    required
                    placeholder="e.g. Daily admission calling targets (<10 min SLA)"
                    value={taskForm.title}
                    onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                    className="mt-1 text-xs h-9"
                  />
                </div>

                <div>
                  <Label className="text-xs font-semibold text-slate-700">Description</Label>
                  <textarea
                    rows={2}
                    placeholder="Execution details, deliverables, expected outcome..."
                    value={taskForm.description}
                    onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                    className={`mt-1 w-full border rounded-xl p-2.5 text-xs outline-none focus:border-emerald-600 ${
                      isLight ? "bg-white border-slate-200" : "bg-zinc-800 border-zinc-700 text-white"
                    }`}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Priority</Label>
                    <select
                      value={taskForm.priority}
                      onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                      className={`mt-1 w-full border rounded-xl px-2.5 py-2 text-xs outline-none ${
                        isLight ? "bg-white border-slate-200" : "bg-zinc-800 border-zinc-700 text-white"
                      }`}
                    >
                      <option value="P0 (Urgent)">🔴 P0 (Urgent SLA)</option>
                      <option value="P1 (High)">🟠 P1 (High Priority)</option>
                      <option value="P2 (Normal)">🔵 P2 (Standard Priority)</option>
                    </select>
                  </div>

                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Due Date</Label>
                    <Input
                      type="date"
                      value={taskForm.timeline_date}
                      onChange={(e) => setTaskForm({ ...taskForm, timeline_date: e.target.value })}
                      className="mt-1 text-xs h-9 font-mono"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-semibold text-slate-700 flex items-center justify-between">
                    <span>Checklist Items</span>
                    <span className="text-[10px] text-slate-400 font-normal">One item per line</span>
                  </Label>
                  <textarea
                    rows={3}
                    placeholder="1) Review incoming inquiries&#10;2) Complete 25 calls with CRM notes&#10;3) Escalate hot leads to mentor"
                    value={taskForm.checklist_text}
                    onChange={(e) => setTaskForm({ ...taskForm, checklist_text: e.target.value })}
                    className={`mt-1 w-full border rounded-xl p-2.5 text-xs font-mono outline-none focus:border-emerald-600 ${
                      isLight ? "bg-white border-slate-200" : "bg-zinc-800 border-zinc-700 text-white"
                    }`}
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowAssignModal(false)}
                    className="text-xs text-slate-500"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submittingTask}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs h-9 rounded-xl px-4"
                  >
                    {submittingTask ? "Assigning..." : "Assign Task to Member"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ===================== ADD TEAM MEMBER MODAL ===================== */}
        {showAddMemberModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-150">
            <div className={`relative w-full max-w-2xl max-h-[90vh] rounded-3xl border shadow-2xl p-6 flex flex-col ${
              isLight ? "bg-white border-slate-200 text-slate-900" : "bg-zinc-900 border-zinc-800 text-white"
            }`}>
              <div className="flex items-center justify-between border-b pb-4 mb-4 border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100">
                    <UserPlus className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Add New Team Member</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Provision employee login ID, credentials, and official role KPIs.
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowAddMemberModal(false)}
                  className="rounded-full h-8 w-8 text-slate-400 hover:text-slate-700"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <form onSubmit={handleAddMember} className="space-y-4 overflow-y-auto pr-1 custom-scrollbar">
                <div className="space-y-4">
                  {/* Full Name */}
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Full Name *</Label>
                    <Input
                      required
                      placeholder="e.g. Rahul Sharma"
                      value={addMemberForm.name}
                      onChange={(e) => setAddMemberForm({ ...addMemberForm, name: e.target.value })}
                      className="mt-1 text-xs h-10 rounded-xl"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                    {/* Role & Responsibilities */}
                    <div>
                      <Label className="text-xs font-semibold text-slate-700">Role / Designation *</Label>
                      <Input
                        required
                        placeholder="e.g. Social media manager, Operation Manager"
                        value={addMemberForm.team_title}
                        onChange={(e) => setAddMemberForm({ 
                          ...addMemberForm, 
                          team_title: e.target.value,
                          core_kpis: e.target.value 
                        })}
                        className="mt-1 text-xs h-10 rounded-xl"
                      />
                    </div>

                    {/* Department */}
                    <div>
                      <Label className="text-xs font-semibold text-slate-700">Department</Label>
                      <Input
                        placeholder="e.g. Marketing & Media, Operations, Growth, Tech"
                        value={addMemberForm.department}
                        onChange={(e) => setAddMemberForm({ ...addMemberForm, department: e.target.value })}
                        className="mt-1 text-xs h-10 rounded-xl"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                    {/* User ID / Email */}
                    <div>
                      <Label className="text-xs font-semibold text-slate-700 flex items-center justify-between">
                        <span>Email ID / User ID *</span>
                        <span className="text-[10px] text-slate-400 font-normal">Login identifier</span>
                      </Label>
                      <Input
                        required
                        placeholder="e.g. rahul@jamadhan.in"
                        value={addMemberForm.login_id}
                        onChange={(e) => setAddMemberForm({ ...addMemberForm, login_id: e.target.value })}
                        className="mt-1 text-xs h-10 rounded-xl font-mono"
                      />
                    </div>

                    {/* Password */}
                    <div>
                      <Label className="text-xs font-semibold text-slate-700 flex items-center justify-between">
                        <span>Password *</span>
                        <span className="text-[10px] text-emerald-600 font-normal">Min 6 characters</span>
                      </Label>
                      <div className="relative mt-1">
                        <Input
                          required
                          type={showAddPassword ? "text" : "password"}
                          placeholder="Assign login password"
                          value={addMemberForm.password}
                          onChange={(e) => setAddMemberForm({ ...addMemberForm, password: e.target.value })}
                          className="text-xs h-10 rounded-xl pr-9 font-mono"
                        />
                        <button
                          type="button"
                          onClick={() => setShowAddPassword(!showAddPassword)}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showAddPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowAddMemberModal(false)}
                    className="text-xs text-slate-500 rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submittingAddMember}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs h-10 px-5 rounded-xl shadow-sm transition-all"
                  >
                    {submittingAddMember ? "Creating Member..." : "Create Team Member"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ===================== EDIT MEMBER / RESET PASSWORD MODAL ===================== */}
        {showEditMemberModal && editMemberTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-150">
            <div className={`relative w-full max-w-lg rounded-3xl border shadow-2xl p-6 ${
              isLight ? "bg-white border-slate-200 text-slate-900" : "bg-zinc-900 border-zinc-800 text-white"
            }`}>
              <div className="flex items-center justify-between border-b pb-4 mb-4 border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-blue-50 text-blue-600 border border-blue-100">
                    <KeyRound className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Edit Member & Reset Password</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{editMemberTarget.name} ({editMemberTarget.email})</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowEditMemberModal(false)}
                  className="rounded-full h-8 w-8 text-slate-400 hover:text-slate-700"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <form onSubmit={handleUpdateMember} className="space-y-4">
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Full Name</Label>
                    <Input
                      required
                      value={editMemberForm.name}
                      onChange={(e) => setEditMemberForm({ ...editMemberForm, name: e.target.value })}
                      className="mt-1 text-xs h-10 rounded-xl"
                    />
                  </div>

                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Role & Responsibilities</Label>
                    <Input
                      required
                      value={editMemberForm.team_title}
                      onChange={(e) => setEditMemberForm({ ...editMemberForm, team_title: e.target.value })}
                      className="mt-1 text-xs h-10 rounded-xl"
                    />
                  </div>

                  <div>
                    <Label className="text-xs font-semibold text-slate-700">Department</Label>
                    <Input
                      placeholder="e.g. Marketing & Media, Operations, Growth, Tech"
                      value={editMemberForm.department || ""}
                      onChange={(e) => setEditMemberForm({ ...editMemberForm, department: e.target.value })}
                      className="mt-1 text-xs h-10 rounded-xl"
                    />
                  </div>

                  <div>
                    <Label className="text-xs font-semibold text-slate-700 flex items-center justify-between">
                      <span>Reset Password</span>
                      <span className="text-[10px] text-slate-400 font-normal">Leave blank to keep unchanged</span>
                    </Label>
                    <Input
                      type="password"
                      placeholder="Enter new password to reset"
                      value={editMemberForm.password}
                      onChange={(e) => setEditMemberForm({ ...editMemberForm, password: e.target.value })}
                      className="mt-1 text-xs h-10 rounded-xl font-mono"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowEditMemberModal(false)}
                    className="text-xs text-slate-500 rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={submittingEditMember}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs h-10 px-5 rounded-xl shadow-sm transition-all"
                  >
                    {submittingEditMember ? "Saving..." : "Save Details"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ===================== DELETE MEMBER CONFIRMATION MODAL ===================== */}
        {memberToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-150">
            <div className={`relative max-w-md w-full rounded-3xl border shadow-2xl p-6 ${
              isLight ? "bg-white border-slate-200 text-slate-900" : "bg-zinc-900 border-zinc-800 text-white"
            }`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2.5 rounded-2xl bg-rose-50 text-rose-600 border border-rose-100">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-base">Remove Member from Roster?</h3>
                  <p className="text-xs text-slate-500">This action requires admin confirmation.</p>
                </div>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed mt-2">
                Are you sure you want to remove <strong>{memberToDelete.name}</strong> ({memberToDelete.email}) from the active team roster? Their account will be deactivated.
              </p>
              <div className="flex items-center justify-end gap-2 mt-5 pt-3 border-t border-slate-100">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMemberToDelete(null)}
                  className="text-xs text-slate-500"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={confirmDeleteMember}
                  className="bg-rose-600 hover:bg-rose-700 text-white font-medium text-xs rounded-xl px-4"
                >
                  Yes, Remove Member
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ===================== DOWNLOAD & PRINTABLE REPORT MODAL ===================== */}
        {downloadReportModal && downloadReportData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-150">
            <div className={`w-full max-w-4xl max-h-[92vh] flex flex-col rounded-3xl border shadow-2xl overflow-hidden ${
              isLight ? "bg-white border-slate-200 text-slate-900" : "bg-zinc-900 border-zinc-800 text-white"
            }`}>
              {/* Header */}
              <div className={`p-5 border-b flex items-center justify-between gap-3 ${
                isLight ? "bg-slate-50/70 border-slate-200" : "bg-zinc-950 border-zinc-800"
              }`}>
                <div>
                  <h3 className="font-bold text-base sm:text-lg">
                    {downloadReportData.period_label}
                  </h3>
                  <p className="text-xs text-slate-500">
                    Team Member: <strong className="text-slate-800">{downloadReportData.member?.name}</strong> ({downloadReportData.member?.team_title})
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={exportReportToCSV}
                    className="text-xs h-8 rounded-xl font-medium"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5 text-emerald-600" /> CSV Export
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => window.print()}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 rounded-xl font-medium"
                  >
                    <Printer className="w-3.5 h-3.5 mr-1.5" /> Print / PDF
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDownloadReportModal(false)}
                    className="rounded-full h-8 w-8 text-slate-400 hover:text-slate-700"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Printable Body */}
              <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-4 custom-scrollbar text-xs">
                {/* Metrics Summary Strip */}
                <div className={`grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 rounded-2xl border ${
                  isLight ? "bg-slate-50/60 border-slate-200/80" : "bg-zinc-950 border-zinc-800"
                }`}>
                  <div>
                    <span className="text-[11px] text-slate-500">Days Present</span>
                    <p className="text-xl font-bold font-mono text-emerald-600 mt-0.5">
                      {downloadReportData.stats?.days_present} / {downloadReportData.period_days}
                    </p>
                  </div>
                  <div>
                    <span className="text-[11px] text-slate-500">Total Hours</span>
                    <p className="text-xl font-bold font-mono text-slate-800 mt-0.5">
                      {downloadReportData.stats?.total_hours}h
                    </p>
                  </div>
                  <div>
                    <span className="text-[11px] text-slate-500">Compliance Rate</span>
                    <p className="text-xl font-bold font-mono text-emerald-600 mt-0.5">
                      {downloadReportData.stats?.attendance_rate}%
                    </p>
                  </div>
                  <div>
                    <span className="text-[11px] text-slate-500">Tasks Completed</span>
                    <p className="text-xl font-bold font-mono text-blue-600 mt-0.5">
                      {downloadReportData.stats?.completed_tasks} / {downloadReportData.stats?.total_tasks}
                    </p>
                  </div>
                </div>

                {/* Table of Records */}
                <div className={`border rounded-2xl overflow-hidden ${
                  isLight ? "border-slate-200/80" : "border-zinc-800"
                }`}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className={`text-[11px] font-semibold uppercase tracking-wider border-b ${
                        isLight ? "bg-slate-50 text-slate-500 border-slate-200" : "bg-zinc-800 text-zinc-400 border-zinc-700"
                      }`}>
                        <tr>
                          <th className="p-3">Date</th>
                          <th className="p-3">In (IST)</th>
                          <th className="p-3">Out (IST)</th>
                          <th className="p-3">Duration</th>
                          <th className="p-3">Status</th>
                          <th className="p-3">Location</th>
                          <th className="p-3">Daily Work Report</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${isLight ? "divide-slate-100" : "divide-zinc-800"}`}>
                        {downloadReportData.records?.map((r, i) => (
                          <tr key={r.date || i} className={isLight ? "hover:bg-slate-50/60" : "hover:bg-zinc-800/40"}>
                            <td className="p-3 font-mono font-medium whitespace-nowrap">
                              {r.display_date} ({r.day})
                            </td>
                            <td className="p-3 font-mono text-emerald-600 font-bold">
                              {r.punch_in_time}
                            </td>
                            <td className="p-3 font-mono text-slate-600">
                              {r.punch_out_time}
                            </td>
                            <td className="p-3 font-mono font-medium">
                              {r.duration}
                            </td>
                            <td className="p-3">
                              <span className={`text-[10px] font-mono px-2 py-0.5 rounded font-medium ${
                                r.status === "present" || r.status === "clocked_in"
                                  ? "bg-emerald-50 text-emerald-700"
                                  : r.status === "weekly_off"
                                  ? "bg-slate-100 text-slate-600"
                                  : "bg-amber-50 text-amber-700"
                              }`}>
                                {r.status}
                              </span>
                            </td>
                            <td className="p-3 text-slate-500 text-[11px] truncate max-w-[140px]">
                              {r.location?.split("(")[0]?.trim() || "Jaipur Campus"}
                            </td>
                            <td className="p-3 text-slate-600 max-w-xs">
                              {r.eod_report ? (
                                <span className="line-clamp-2 italic">{r.eod_report}</span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===================== BIOMETRIC SELFIE ZOOM MODAL ===================== */}
        {inspectedImage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-in fade-in duration-150">
            <div className={`relative max-w-md w-full rounded-3xl border p-5 shadow-2xl flex flex-col items-center ${
              isLight ? "bg-white border-slate-200" : "bg-zinc-900 border-zinc-800"
            }`}>
              <div className="flex items-center justify-between w-full pb-3 border-b mb-3 border-slate-100">
                <span className="text-xs font-mono uppercase tracking-wider text-emerald-700 font-bold flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4" /> Biometric Selfie Verification
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setInspectedImage(null)}
                  className="rounded-full h-8 w-8 text-slate-400 hover:text-slate-700"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <img
                src={inspectedImage}
                alt="Enlarged selfie"
                className="w-full h-auto max-h-[70vh] rounded-2xl object-contain border border-slate-200 shadow-md"
              />
              <p className="text-[11px] text-slate-500 mt-3 text-center">
                High-precision biometric photo captured on-site during punch action.
              </p>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}
